import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlay,
  faStop,
  faCode,
  faTerminal,
  faSpinner,
  faSave,
  faTrash,
  faFolder,
  faPlus,
  faSkullCrossbones,
  faClock,
  faFlask,
  faBook,
  faChartLine,
  faSearch,
  faExchangeAlt,
  faAlignLeft,
  faUndo,
  faRedo,
  faMinus,
  faFont,
  faFileCode,
  faTextWidth,
  faSun,
  faMoon,
  faFeather,
  faExpand,
  faCompress,
  faExternalLinkAlt,
  faTimes,
  faRobot,
  faCheck,
  faExclamationTriangle,
  faChevronDown,
  faChevronRight,
  faAnglesLeft,
  faAnglesRight,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { algorithmApi } from '../api/algorithmApi';
import { schwabApi } from '../api/schwabApi';
import { useRisk } from '../contexts/RiskContext';
import { useAlerts } from '../contexts/AlertsContext';
import AlgoSignalChart from '../components/AlgoSignalChart';
import AlgoCodeEditor from '../components/AlgoCodeEditor';
import AlgoWorkbenchDocs from '../components/AlgoWorkbenchDocs';
import { checkAlgoScriptSyntax } from '../utils/algoSyntaxCheck';
import { formatEtDateTime, formatEtRange } from '../utils/etTime';

const IDE_THEME_KEY = 'algo-workbench-theme';
const IDE_EXPLORER_OPEN_KEY = 'lt-algo-explorer-open';
const IDE_TEMPLATES_OPEN_KEY = 'lt-algo-templates-open';
const IDE_THEMES = ['light', 'dark', 'robinhood'];
const nextIdeTheme = (current) => {
  const i = IDE_THEMES.indexOf(current);
  return IDE_THEMES[(i < 0 ? 0 : i + 1) % IDE_THEMES.length];
};
const IDE_PANEL_H_KEY = 'lt-algo-ide-panel-h';
const IDE_PANEL_H_DEFAULT = 280;
const IDE_PANEL_H_MIN = 140;
const IDE_POPOUT_NAME = 'lt-algo-editor';
const parseUsdField = (value) => {
  if (value === '' || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};
const usdFieldFromAlgo = (value) => {
  const n = parseUsdField(value);
  return n == null ? '' : String(n);
};
const isAlgoPopoutWindow = () => {
  try {
    return new URLSearchParams(window.location.search).get('popout') === '1';
  } catch {
    return false;
  }
};

const DEFAULT_SCRIPT = `const fastPeriod = 20;
const slowPeriod = 50;
const riskPct = 1;
// \`symbol\` / \`symbols\` come from the IDE Symbols field

function sma(closes, period) {
  if (closes.length < period) return null;
  const slice = closes.slice(-period);
  return slice.reduce((a, b) => a + b, 0) / period;
}

async function runTradingBot() {
  if (helpers.manageExistingPositions) {
    await helpers.adoptExistingPositions([symbol]);
  }

  const clock = await helpers.getClock();
  if (helpers.tradeOnlyWhenOpen && !clock.is_open) {
    console.log('Market closed — skipping');
    return;
  }

  console.log(\`Checking \${symbol}...\`);
  const barsResponse = await alpaca.getBarsV2([symbol], { timeframe: '1Day', limit: 120 });
  const bars = barsResponse[symbol] || [];
  const closes = bars.map((b) => b.ClosePrice ?? b.c).filter((n) => Number.isFinite(n));
  console.log(\`Bars loaded: \${closes.length} (need \${slowPeriod + 2})\`);
  if (closes.length < slowPeriod + 2) {
    console.log('Not enough data');
    return;
  }

  const prevFast = sma(closes.slice(0, -1), fastPeriod);
  const prevSlow = sma(closes.slice(0, -1), slowPeriod);
  const fast = sma(closes, fastPeriod);
  const slow = sma(closes, slowPeriod);
  const price = closes[closes.length - 1];
  console.log(\`$\${price.toFixed(2)} | SMA\${fastPeriod}=$\${fast.toFixed(2)} | SMA\${slowPeriod}=$\${slow.toFixed(2)}\`);

  let positionQty = 0;
  try {
    positionQty = parseFloat((await alpaca.getPosition(symbol)).qty) || 0;
  } catch {
    positionQty = 0;
  }

  const golden = prevFast <= prevSlow && fast > slow;
  const death = prevFast >= prevSlow && fast < slow;

  if (golden && positionQty <= 0) {
    const size = await helpers.sizeByRiskPct(riskPct, price);
    console.log(\`GOLDEN CROSS → bracket buy \${size}\`);
    helpers.emitSignal({ kind: 'golden_cross', label: 'Golden cross', symbol, price, side: 'buy' });
    await helpers.createBracketOrder({
      symbol, qty: size, side: 'buy', takeProfitPct: 3, stopLossPct: 1.5,
    });
  } else if (death && positionQty > 0) {
    if (!(helpers.manageExistingPositions || helpers.isAlgoOwned(symbol))) {
      console.log('DEATH CROSS ignored — enable Manage existing positions to exit this holding');
      return;
    }
    console.log('DEATH CROSS → flatten');
    helpers.emitSignal({ kind: 'death_cross', label: 'Death cross', symbol, price, side: 'sell' });
    await alpaca.closePosition(symbol);
  } else {
    console.log('No crossover');
  }
}

await runTradingBot();`;

function defaultBacktestRange(timeframe = '1Day') {
  const end = new Date();
  const start = new Date();
  if (timeframe === '1Min') start.setDate(start.getDate() - 3);
  else if (timeframe === '5Min' || timeframe === '15Min') start.setDate(start.getDate() - 14);
  else if (timeframe === '1Hour') start.setMonth(start.getMonth() - 2);
  else start.setFullYear(start.getFullYear() - 1);
  const iso = (d) => d.toISOString().slice(0, 10);
  return {
    start: iso(start),
    end: iso(end),
    startTime: '09:30',
    endTime: '16:00',
  };
}

function isIntradayBacktestTf(tf) {
  return /Min|Hour/i.test(String(tf || ''));
}

function EquitySparkline({ points = [], height = 120 }) {
  if (!points.length) return null;
  const vals = points.map((p) => p.equity);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const w = 600;
  const pad = 4;
  const coords = points
    .map((p, i) => {
      const x = pad + (i / Math.max(points.length - 1, 1)) * (w - pad * 2);
      const y = pad + (1 - (p.equity - min) / span) * (height - pad * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  const up = vals[vals.length - 1] >= vals[0];
  return (
    <svg viewBox={`0 0 ${w} ${height}`} className="w-full h-28" preserveAspectRatio="none">
      <polyline
        fill="none"
        stroke={up ? '#059669' : '#dc2626'}
        strokeWidth="2"
        points={coords}
      />
    </svg>
  );
}

const AlgorithmicTradingPage = () => {
  const { accessToken, refreshToken: refreshAuthToken } = useAuth();
  const { settings: riskSettings, updateSettings: updateRiskSettings, dayPl, algoMaxLossUsd, account } = useRisk();
  const { emitEvent, requestPermission } = useAlerts();
  const [script, setScript] = useState(DEFAULT_SCRIPT);
  const [logsByAlgo, setLogsByAlgo] = useState({});
  const [systemLogs, setSystemLogs] = useState([]);
  const [logTab, setLogTab] = useState(null);
  const [intervalSeconds, setIntervalSeconds] = useState(60);
  const [runBudgetUsd, setRunBudgetUsd] = useState('');
  const [runMaxLossUsd, setRunMaxLossUsd] = useState('');
  const [syntaxCheck, setSyntaxCheck] = useState(() => checkAlgoScriptSyntax(DEFAULT_SCRIPT));
  const [ws, setWs] = useState(null);
  const wsRef = useRef(null);
  const attachedIdsRef = useRef(new Set());
  const [attachedIds, setAttachedIds] = useState(() => new Set());
  const [algorithmId, setAlgorithmId] = useState(() => {
    try {
      return localStorage.getItem('lt-active-algo-id') || `algo-${Date.now()}`;
    } catch {
      return `algo-${Date.now()}`;
    }
  });
  const logsEndRef = useRef(null);
  const [tradeOnlyWhenOpen, setTradeOnlyWhenOpen] = useState(() => {
    try {
      const v = localStorage.getItem('lt-algo-trade-only-when-open');
      if (v === '0' || v === 'false') return false;
      if (v === '1' || v === 'true') return true;
    } catch { /* ignore */ }
    return true;
  });
  const [manageExistingPositions, setManageExistingPositions] = useState(false);
  const [skipOpenClose, setSkipOpenClose] = useState(false);
  const [skipOpenMinutes, setSkipOpenMinutes] = useState(15);
  const [skipCloseMinutes, setSkipCloseMinutes] = useState(15);
  const [useCustomHours, setUseCustomHours] = useState(false);
  const [scheduleStartTime, setScheduleStartTime] = useState('09:45');
  const [scheduleEndTime, setScheduleEndTime] = useState('15:45');
  const [scheduleDays, setScheduleDays] = useState([1, 2, 3, 4, 5]);
  const [scheduleDateFrom, setScheduleDateFrom] = useState('');
  const [scheduleDateTo, setScheduleDateTo] = useState('');
  const [showSchedulePanel, setShowSchedulePanel] = useState(false);
  const [executionVenue, setExecutionVenue] = useState(() => {
    try {
      return localStorage.getItem('lt-algo-venue') === 'schwab' ? 'schwab' : 'alpaca';
    } catch {
      return 'alpaca';
    }
  });
  const [schwabConnected, setSchwabConnected] = useState(false);
  const [schwabAccounts, setSchwabAccounts] = useState([]);
  const [schwabAccountNumber, setSchwabAccountNumber] = useState(() => {
    try {
      return localStorage.getItem('lt-algo-schwab-account') || '';
    } catch {
      return '';
    }
  });
  const [schwabAccountsLoading, setSchwabAccountsLoading] = useState(false);
  const [workspace, setWorkspace] = useState('workbench'); // workbench | twap | reference
  const [idePopout] = useState(() => isAlgoPopoutWindow());
  const [ideFullscreen, setIdeFullscreen] = useState(false);
  const [ideBottomTab, setIdeBottomTab] = useState('output'); // output | backtest | chart
  const [idePanelOpen, setIdePanelOpen] = useState(true);
  const [idePanelHeight, setIdePanelHeight] = useState(() => {
    try {
      const n = parseInt(localStorage.getItem(IDE_PANEL_H_KEY), 10);
      return Number.isFinite(n) ? Math.min(720, Math.max(IDE_PANEL_H_MIN, n)) : IDE_PANEL_H_DEFAULT;
    } catch {
      return IDE_PANEL_H_DEFAULT;
    }
  });
  const editorPaneRef = useRef(null);
  const [ideDocsTab, setIdeDocsTab] = useState('guide');
  const [ideDocsOpen, setIdeDocsOpen] = useState(() => {
    try {
      const v = localStorage.getItem('lt-algo-docs-open');
      return v == null ? true : v === '1';
    } catch {
      return true;
    }
  });
  const [backtestSymbol, setBacktestSymbol] = useState('');
  const [deskSymbols, setDeskSymbols] = useState(() => {
    try {
      return localStorage.getItem('lt-algo-desk-symbols') || 'AAPL';
    } catch {
      return 'AAPL';
    }
  });
  const [backtestTimeframe, setBacktestTimeframe] = useState('1Day');
  const [backtestStart, setBacktestStart] = useState(() => defaultBacktestRange().start);
  const [backtestEnd, setBacktestEnd] = useState(() => defaultBacktestRange().end);
  const [backtestStartTime, setBacktestStartTime] = useState('09:30');
  const [backtestEndTime, setBacktestEndTime] = useState('16:00');
  const [backtestCash, setBacktestCash] = useState('100000');
  const [backtestScalePositions, setBacktestScalePositions] = useState(true);
  const [backtestForceRefresh, setBacktestForceRefresh] = useState(false);
  const [backtestRunning, setBacktestRunning] = useState(false);
  const [backtestError, setBacktestError] = useState(null);
  const [backtestResult, setBacktestResult] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [twapForm, setTwapForm] = useState({
    symbol: 'AAPL',
    side: 'buy',
    qty: '50',
    durationMinutes: '30',
    slices: '10',
    limitOffset: '',
  });
  const [activeTwapId, setActiveTwapId] = useState(null);
  const [runningJobs, setRunningJobs] = useState([]);
  const [twapJobs, setTwapJobs] = useState([]);
  const [signalEvents, setSignalEvents] = useState([]);
  const [chartSymbol, setChartSymbol] = useState('AAPL');
  
  // Algorithm management state
  const [algorithms, setAlgorithms] = useState([]);
  const [selectedAlgorithm, setSelectedAlgorithm] = useState(null);
  const [algorithmName, setAlgorithmName] = useState('');
  const [algorithmDescription, setAlgorithmDescription] = useState('');
  const [loadingAlgorithms, setLoadingAlgorithms] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [algoExplorerQuery, setAlgoExplorerQuery] = useState('');
  const [explorerOpen, setExplorerOpen] = useState(() => {
    try {
      const v = localStorage.getItem(IDE_EXPLORER_OPEN_KEY);
      if (v === '0') return false;
      if (v === '1') return true;
    } catch {
      /* ignore */
    }
    return true;
  });
  const [templatesOpen, setTemplatesOpen] = useState(() => {
    try {
      const v = localStorage.getItem(IDE_TEMPLATES_OPEN_KEY);
      if (v === '1') return true;
      if (v === '0') return false;
    } catch {
      /* ignore */
    }
    return false; // collapsed by default
  });
  const [editorFontSize, setEditorFontSize] = useState(13);
  const [editorWordWrap, setEditorWordWrap] = useState(false);
  const [ideTheme, setIdeTheme] = useState(() => {
    try {
      const saved = localStorage.getItem(IDE_THEME_KEY);
      if (saved === 'light' || saved === 'dark' || saved === 'robinhood') return saved;
      return 'light';
    } catch {
      return 'light';
    }
  });
  const ideDark = ideTheme !== 'light';
  const [savedScriptBaseline, setSavedScriptBaseline] = useState(DEFAULT_SCRIPT);
  const codeEditorRef = useRef(null);
  const ideShellRef = useRef(null);

  const persistIdePanelHeight = useCallback((h) => {
    try {
      localStorage.setItem(IDE_PANEL_H_KEY, String(h));
    } catch {
      /* ignore */
    }
  }, []);

  const startIdePanelResize = useCallback((e) => {
    if (!idePanelOpen) return;
    e.preventDefault();
    const startY = e.clientY;
    const startH = idePanelHeight;
    const paneH = editorPaneRef.current?.clientHeight || 720;
    const maxH = Math.max(IDE_PANEL_H_MIN, paneH - 160);
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev) => {
      const next = Math.min(maxH, Math.max(IDE_PANEL_H_MIN, startH + (startY - ev.clientY)));
      setIdePanelHeight(next);
    };
    const onUp = (ev) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
      const next = Math.min(maxH, Math.max(IDE_PANEL_H_MIN, startH + (startY - ev.clientY)));
      setIdePanelHeight(next);
      persistIdePanelHeight(next);
      window.setTimeout(() => codeEditorRef.current?.layout?.(), 30);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [idePanelOpen, idePanelHeight, persistIdePanelHeight]);

  const openIdePanel = useCallback((tab = 'output') => {
    setWorkspace('workbench');
    setIdeBottomTab(tab);
    setIdePanelOpen(true);
  }, []);

  const toggleIdeFullscreen = useCallback(() => {
    setIdeFullscreen((v) => !v);
  }, []);

  const openIdePopout = useCallback(() => {
    if (idePopout) return;
    const url = `${window.location.origin}/algorithm?popout=1`;
    const features = 'noopener,noreferrer,width=1440,height=960';
    const child = window.open(url, IDE_POPOUT_NAME, features);
    if (child) {
      try {
        child.focus();
      } catch {
        /* ignore */
      }
    } else {
      setSystemLogs((prev) => [
        ...prev,
        {
          type: 'warn',
          message: 'Pop-out blocked — allow pop-ups, or open /algorithm?popout=1 manually.',
          timestamp: new Date().toISOString(),
        },
      ]);
    }
  }, [idePopout]);

  useEffect(() => {
    if (!ideFullscreen && !idePopout) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !idePopout) setIdeFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ideFullscreen, idePopout]);

  useEffect(() => {
    if (idePopout) {
      setWorkspace('workbench');
      document.title = 'Algo Editor · LionTrading';
    }
  }, [idePopout]);

  useEffect(() => {
    if (!ideFullscreen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [ideFullscreen]);

  // Monaco caret hit-testing drifts if layout isn't refreshed after shell mode changes.
  useEffect(() => {
    const id = window.setTimeout(() => {
      codeEditorRef.current?.layout?.();
    }, 30);
    const id2 = window.setTimeout(() => {
      codeEditorRef.current?.layout?.();
    }, 120);
    return () => {
      window.clearTimeout(id);
      window.clearTimeout(id2);
    };
  }, [ideFullscreen, idePopout, idePanelOpen, ideBottomTab, ideDocsOpen, idePanelHeight]);

  const scriptJobs = runningJobs.filter((j) => j.kind === 'script');
  const scriptDirty = script !== savedScriptBaseline;

  useEffect(() => {
    try {
      localStorage.setItem(IDE_THEME_KEY, ideTheme);
    } catch {
      /* ignore */
    }
  }, [ideTheme]);
  useEffect(() => {
    try {
      localStorage.setItem('lt-algo-docs-open', ideDocsOpen ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [ideDocsOpen]);
  useEffect(() => {
    try {
      localStorage.setItem(IDE_EXPLORER_OPEN_KEY, explorerOpen ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [explorerOpen]);
  useEffect(() => {
    try {
      localStorage.setItem(IDE_TEMPLATES_OPEN_KEY, templatesOpen ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [templatesOpen]);
  const filteredAlgorithms = useMemo(() => {
    const q = algoExplorerQuery.trim().toLowerCase();
    if (!q) return algorithms;
    return algorithms.filter(
      (a) =>
        String(a.name || '').toLowerCase().includes(q) ||
        String(a.description || '').toLowerCase().includes(q)
    );
  }, [algorithms, algoExplorerQuery]);
  const filteredTemplates = useMemo(() => {
    const q = algoExplorerQuery.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(
      (t) =>
        String(t.name || '').toLowerCase().includes(q) ||
        String(t.category || '').toLowerCase().includes(q) ||
        String(t.description || '').toLowerCase().includes(q)
    );
  }, [templates, algoExplorerQuery]);
  const activeLogs =
    logTab === '__system__'
      ? systemLogs
      : logTab
        ? logsByAlgo[logTab] || []
        : systemLogs;

  const scrollToBottom = () => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [activeLogs, logTab]);

  const pushLog = (algoId, type, message, extra = {}) => {
    const entry = {
      type,
      message,
      timestamp: new Date().toISOString(),
      algorithmId: algoId || null,
      ...extra,
    };
    if (!algoId) {
      setSystemLogs((prev) => [...prev, entry]);
      return;
    }
    setLogsByAlgo((prev) => ({
      ...prev,
      [algoId]: [...(prev[algoId] || []), entry],
    }));
  };

  /** UI-level status into the selected (or system) log pane */
  const addLog = (type, message) => {
    pushLog(logTab && logTab !== '__system__' ? logTab : null, type, message);
  };

  const rememberLabel = (id, label) => {
    if (!id || !label) return;
    try {
      const raw = localStorage.getItem('lt-algo-labels');
      const map = raw ? JSON.parse(raw) : {};
      map[id] = String(label).slice(0, 80);
      localStorage.setItem('lt-algo-labels', JSON.stringify(map));
    } catch {
      /* ignore */
    }
  };

  const getStoredLabel = (id) => {
    try {
      const raw = localStorage.getItem('lt-algo-labels');
      const map = raw ? JSON.parse(raw) : {};
      return map[id] || null;
    } catch {
      return null;
    }
  };

  const jobLabel = (job) => {
    if (!job) return 'Algorithm';
    return (
      job.label ||
      getStoredLabel(job.id) ||
      parseScriptSymbol(job.scriptPreview) ||
      job.id
    );
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSyntaxCheck(checkAlgoScriptSyntax(script));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [script]);

  const runSyntaxCheck = useCallback(() => {
    const result = checkAlgoScriptSyntax(script);
    setSyntaxCheck(result);
    if (result.ok) {
      pushLog(null, 'status', 'Syntax OK — script compiles as an async workbench function.');
    } else {
      const loc =
        result.line != null
          ? ` (line ${result.line}${result.column != null ? `:${result.column}` : ''})`
          : '';
      pushLog(null, 'error', `Syntax error${loc}: ${result.message}`);
      if (result.line != null) {
        codeEditorRef.current?.gotoLine?.(result.line, result.column || 1);
      }
    }
    return result;
  }, [script]);

  const buildSchedulePayload = () => ({
    tradeOnlyWhenOpen,
    skipOpenMinutes: skipOpenClose ? Number(skipOpenMinutes) || 0 : 0,
    skipCloseMinutes: skipOpenClose ? Number(skipCloseMinutes) || 0 : 0,
    useCustomHours,
    startTime: scheduleStartTime || '09:45',
    endTime: scheduleEndTime || '15:45',
    daysOfWeek: scheduleDays.length ? scheduleDays : [1, 2, 3, 4, 5],
    dateFrom: scheduleDateFrom || null,
    dateTo: scheduleDateTo || null,
  });

  const toggleScheduleDay = (day) => {
    setScheduleDays((prev) => {
      if (prev.includes(day)) {
        const next = prev.filter((d) => d !== day);
        return next.length ? next : prev; // keep at least one day
      }
      return [...prev, day].sort((a, b) => a - b);
    });
  };

  const parseDeskSymbols = (input) => {
    const list = String(input || '')
      .split(/[\s,;]+/)
      .map((s) => s.trim().toUpperCase())
      .filter((s) => /^[\^A-Z][A-Z0-9.=\-^]{0,14}$/.test(s));
    return {
      symbol: list[0] || '',
      symbols: list,
      symbolY: list[0] || '',
      symbolX: list[1] || '',
    };
  };

  const parsedDesk = useMemo(() => parseDeskSymbols(deskSymbols), [deskSymbols]);

  const backtestChartRange = useMemo(() => {
    if (!backtestResult?.start || !backtestResult?.end) return null;
    return {
      start: backtestResult.start,
      end: backtestResult.end,
      timeframe: backtestResult.timeframe || '1Day',
    };
  }, [backtestResult]);

  const backtestChartEvents = useMemo(() => {
    if (!backtestResult) return [];
    const signals = Array.isArray(backtestResult.signals) ? backtestResult.signals : [];
    if (signals.length) {
      return signals.map((s) => ({
        timestamp: s.time,
        kind: s.kind || 'signal',
        label: s.label || s.kind || 'signal',
        symbol: s.symbol,
        price: s.price,
        side: s.side,
      }));
    }
    // Fallback: plot fills when the script never called emitSignal
    return (backtestResult.trades || []).map((t) => ({
      timestamp: t.time,
      kind: t.side === 'buy' ? 'order' : 'exit',
      label: `${String(t.side || '').toUpperCase()} ${t.qty}${t.reason ? ` · ${t.reason}` : ''}`,
      symbol: t.symbol,
      price: t.price,
      side: t.side,
    }));
  }, [backtestResult]);

  const applyDeskSymbols = (value) => {
    const next = String(value || '').toUpperCase();
    setDeskSymbols(next);
    try {
      localStorage.setItem('lt-algo-desk-symbols', next);
    } catch { /* ignore */ }
    const parsed = parseDeskSymbols(next);
    if (parsed.symbol) {
      setChartSymbol(parsed.symbol);
      setBacktestSymbol(parsed.symbol);
      setTwapForm((f) => ({ ...f, symbol: parsed.symbol }));
    }
  };

  const parseScriptSymbol = (code) => {
    const src = String(code || '');
    const patterns = [
      /(?:const|let|var)\s+symbol\s*=\s*['"]([A-Za-z0-9.=\-^]+)['"]/i,
      /(?:const|let|var)\s+ticker\s*=\s*['"]([A-Za-z0-9.=\-^]+)['"]/i,
      /(?:const|let|var)\s+symbolY\s*=\s*['"]([A-Za-z0-9.=\-^]+)['"]/i,
    ];
    for (const re of patterns) {
      const m = src.match(re);
      if (m) return m[1].toUpperCase();
    }
    return null;
  };

  const parseScriptSymbolsList = (code) => {
    const src = String(code || '');
    const found = [];
    const push = (s) => {
      const u = String(s || '').toUpperCase();
      if (u && !found.includes(u)) found.push(u);
    };
    for (const re of [
      /(?:const|let|var)\s+symbolY\s*=\s*['"]([A-Za-z0-9.=\-^]+)['"]/i,
      /(?:const|let|var)\s+symbolX\s*=\s*['"]([A-Za-z0-9.=\-^]+)['"]/i,
      /(?:const|let|var)\s+symbol\s*=\s*['"]([A-Za-z0-9.=\-^]+)['"]/i,
      /(?:const|let|var)\s+symbols\s*=\s*['"]([^'"]+)['"]/i,
    ]) {
      const m = src.match(re);
      if (!m) continue;
      String(m[1])
        .split(/[\s,;]+/)
        .forEach(push);
    }
    return found;
  };

  useEffect(() => {
    if (parsedDesk.symbol) setChartSymbol(parsedDesk.symbol);
  }, [parsedDesk.symbol]);

  // Load algorithms on mount
  useEffect(() => {
    loadAlgorithms();
    algorithmApi.getTemplates()
      .then((res) => setTemplates(res.data || []))
      .catch((err) => console.error('Failed to load templates:', err));
    const refreshRunning = () => {
      algorithmApi.getRunning()
        .then((res) => {
          const all = res.algorithms || [];
          setRunningJobs(all);
          const scripts = all.filter((j) => j.kind === 'script');
          if (scripts.length) {
            scripts.forEach((s) => {
              if (s.label) rememberLabel(s.id, s.label);
            });
            const saved = (() => {
              try { return localStorage.getItem('lt-active-algo-id'); } catch { return null; }
            })();
            setLogTab((prev) => {
              if (prev && (prev === '__system__' || scripts.some((s) => s.id === prev) || logsByAlgo[prev])) {
                return prev;
              }
              const match = scripts.find((s) => s.id === saved) || scripts[0];
              return match?.id || prev;
            });
            const match = scripts.find((s) => s.id === saved) || scripts[0];
            if (match?.id) setAlgorithmId(match.id);
          }
        })
        .catch(() => {});
      algorithmApi.listTwapJobs()
        .then((res) => setTwapJobs(res.data || []))
        .catch(() => {});
    };
    refreshRunning();
    const t = setInterval(refreshRunning, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoadTemplate = async (templateId) => {
    if (!templateId) return;
    try {
      const res = await algorithmApi.getTemplate(templateId);
      const template = res.data;
      if (!template?.script) return;
      setScript(template.script);
      setSavedScriptBaseline(template.script);
      setSelectedTemplateId(templateId);
      setSignalEvents([]);
      if (template.defaultIntervalSeconds) {
        setIntervalSeconds(template.defaultIntervalSeconds);
      }
      setSelectedAlgorithm(null);
      setAlgorithmName(template.name);
      setAlgorithmDescription(template.description || '');
      const p = template.params || {};
      if (p.symbolY && p.symbolX) applyDeskSymbols(`${p.symbolY}, ${p.symbolX}`);
      else if (p.symbols) applyDeskSymbols(p.symbols);
      else if (p.symbol) applyDeskSymbols(p.symbol);
      else {
        const fromScript = parseScriptSymbolsList(template.script);
        if (fromScript.length) applyDeskSymbols(fromScript.join(', '));
      }
      addLog('status', `Loaded template: ${template.name} — using IDE Symbols field`);
    } catch (err) {
      addLog('error', err.message || 'Failed to load template');
    }
  };

  const loadAlgorithms = async () => {
    try {
      setLoadingAlgorithms(true);
      const response = await algorithmApi.getAlgorithms();
      setAlgorithms(response.data || []);
    } catch (error) {
      console.error('Error loading algorithms:', error);
      addLog('error', `Failed to load algorithms: ${error.message}`);
    } finally {
      setLoadingAlgorithms(false);
    }
  };

  const handleLoadAlgorithm = async (algorithm) => {
    try {
      const response = await algorithmApi.getAlgorithm(algorithm.id);
      const algo = response.data;
      setScript(algo.script);
      setSavedScriptBaseline(algo.script || '');
      setIntervalSeconds(algo.interval_seconds || 60);
      setRunBudgetUsd(usdFieldFromAlgo(algo.run_budget_usd ?? algo.runBudgetUsd));
      setRunMaxLossUsd(usdFieldFromAlgo(algo.run_max_loss_usd ?? algo.runMaxLossUsd));
      setSelectedAlgorithm(algo);
      setSelectedTemplateId('');
      setAlgorithmName(algo.name);
      setAlgorithmDescription(algo.description || '');
      const fromScript = parseScriptSymbolsList(algo.script);
      if (fromScript.length) applyDeskSymbols(fromScript.join(', '));
      addLog('status', `Loaded algorithm: ${algo.name}`);
    } catch (error) {
      console.error('Error loading algorithm:', error);
      addLog('error', `Failed to load algorithm: ${error.message}`);
    }
  };

  const handleSaveAlgorithm = async () => {
    if (!algorithmName.trim()) {
      pushLog(null, 'error', 'Algorithm name is required');
      setLogTab('__system__');
      return;
    }

    try {
      setSaving(true);
      const algorithmData = {
        name: algorithmName.trim(),
        description: algorithmDescription,
        script,
        intervalSeconds,
        runBudgetUsd: parseUsdField(runBudgetUsd),
        runMaxLossUsd: parseUsdField(runMaxLossUsd),
      };

      if (selectedAlgorithm?.id) {
        const res = await algorithmApi.updateAlgorithm(selectedAlgorithm.id, algorithmData);
        const updated = res.data || { ...selectedAlgorithm, ...algorithmData, interval_seconds: intervalSeconds };
        setSelectedAlgorithm(updated);
        pushLog(null, 'status', `Algorithm "${algorithmName}" updated successfully`);
      } else {
        const res = await algorithmApi.createAlgorithm(algorithmData);
        const created = res.data;
        if (created) {
          setSelectedAlgorithm(created);
          setAlgorithmName(created.name || algorithmName);
          setAlgorithmDescription(created.description || algorithmDescription);
        }
        pushLog(null, 'status', `Algorithm "${algorithmName}" saved successfully`);
      }

      setShowSaveModal(false);
      setSavedScriptBaseline(script);
      setLogTab('__system__');
      await loadAlgorithms();
    } catch (error) {
      console.error('Error saving algorithm:', error);
      pushLog(
        null,
        'error',
        `Failed to save algorithm: ${error.response?.data?.error || error.message}`
      );
      setLogTab('__system__');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAlgorithm = async (algorithm) => {
    if (!window.confirm(`Are you sure you want to delete "${algorithm.name}"?`)) {
      return;
    }

    try {
      await algorithmApi.deleteAlgorithm(algorithm.id);
      addLog('status', `Algorithm "${algorithm.name}" deleted`);
      
      // Clear selection if deleted algorithm was selected
      if (selectedAlgorithm && selectedAlgorithm.id === algorithm.id) {
        setSelectedAlgorithm(null);
        setScript(DEFAULT_SCRIPT);
        setSavedScriptBaseline(DEFAULT_SCRIPT);
        setAlgorithmName('');
        setAlgorithmDescription('');
        setRunBudgetUsd('');
        setRunMaxLossUsd('');
      }
      
      await loadAlgorithms();
    } catch (error) {
      console.error('Error deleting algorithm:', error);
      addLog('error', `Failed to delete algorithm: ${error.message}`);
    }
  };

  const handleNewAlgorithm = () => {
    setSelectedAlgorithm(null);
    setSelectedTemplateId('');
    setScript('');
    setSavedScriptBaseline('');
    setAlgorithmName('');
    setAlgorithmDescription('');
    setIntervalSeconds(60);
    setRunBudgetUsd('');
    setRunMaxLossUsd('');
    setLogTab('__system__');
    pushLog(null, 'status', 'New custom algorithm draft — name it and save to add it to your library.');
    setShowSaveModal(true);
  };

  const handleKillAll = async (flatten = false) => {
    const msg = flatten
      ? 'Stop all algorithms/TWAP, cancel their open orders, and close ONLY positions opened by algorithms (manual positions stay)?'
      : 'Stop all algorithms/TWAP and cancel open orders on algo-tracked symbols? (Positions stay open.)';
    if (!window.confirm(msg)) return;
    try {
      const res = await algorithmApi.killAll({
        cancelOrders: true,
        flatten,
        venue: executionVenue,
        accountNumber: executionVenue === 'schwab' ? schwabAccountNumber || null : null,
      });
      const closed = res.closedPositions || [];
      const closedMsg = flatten
        ? closed.length
          ? `; closed algo qty: ${closed.map((c) => (c.error ? `${c.symbol}(${c.error})` : `${c.side} ${c.qty} ${c.symbol}`)).join(', ')}`
          : '; no algo-owned positions to close'
        : '';
      addLog(
        'status',
        `Kill switch: stopped ${(res.stoppedScripts || []).length} scripts, ${(res.stoppedTwap || []).length} TWAPs, cancelled ${res.cancelledOrders || 0} algo orders${closedMsg}`
      );
      setActiveTwapId(null);
      attachedIdsRef.current = new Set();
      setAttachedIds(new Set());
      if (wsRef.current) {
        try { wsRef.current.close(); } catch { /* ignore */ }
        wsRef.current = null;
      }
      if (ws) {
        try { ws.close(); } catch { /* ignore */ }
        setWs(null);
      }
      const running = await algorithmApi.getRunning().catch(() => ({ algorithms: [] }));
      setRunningJobs(running.algorithms || []);
    } catch (err) {
      addLog('error', err.message || 'Kill-all failed');
    }
  };

  const handleStartTwap = async () => {
    try {
      const qty = Number(twapForm.qty);
      if (!twapForm.symbol?.trim() || !Number.isFinite(qty) || qty <= 0) {
        addLog('error', 'TWAP needs a symbol and positive qty');
        return;
      }
      if (executionVenue === 'schwab' && !schwabAccountNumber) {
        addLog('error', 'Select a Schwab account before starting TWAP');
        return;
      }
      const res = await algorithmApi.startTwap({
        symbol: twapForm.symbol.trim().toUpperCase(),
        side: twapForm.side,
        qty,
        durationMinutes: Number(twapForm.durationMinutes) || 30,
        slices: Number(twapForm.slices) || 10,
        limitOffset: twapForm.limitOffset === '' ? null : Number(twapForm.limitOffset),
        tradeOnlyWhenOpen,
        riskSettings,
        venue: executionVenue,
        accountNumber: executionVenue === 'schwab' ? schwabAccountNumber || null : null,
      });
      const job = res.data;
      setActiveTwapId(job?.id || null);
      setChartSymbol(String(job?.symbol || twapForm.symbol).toUpperCase());
      addLog(
        'status',
        `TWAP started on SERVER: ${job?.symbol} ${job?.side} ${job?.qty} over ${job?.durationMinutes}m (${job?.slices} slices). Watch the TWAP dashboard for each slice price/state.`
      );
      const jobs = await algorithmApi.listTwapJobs().catch(() => ({ data: [] }));
      setTwapJobs(jobs.data || []);
    } catch (err) {
      addLog('error', err.message || 'TWAP start failed');
    }
  };

  const handleStopTwap = async (idOverride = null) => {
    const id = idOverride || activeTwapId;
    if (!id) {
      addLog('warn', 'No active TWAP id from this session');
      return;
    }
    try {
      await algorithmApi.stopTwap(id);
      addLog('status', `TWAP stopped: ${id}`);
      if (activeTwapId === id) setActiveTwapId(null);
      const jobs = await algorithmApi.listTwapJobs().catch(() => ({ data: [] }));
      setTwapJobs(jobs.data || []);
    } catch (err) {
      addLog('error', err.message || 'TWAP stop failed');
    }
  };

  const resolveWsUrl = (token) => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    let host;
    let port = '';
    if (process.env.NODE_ENV === 'production') {
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      try {
        const url = new URL(apiUrl);
        host = url.hostname;
        if (url.port && url.port !== '80' && url.port !== '443') port = `:${url.port}`;
      } catch {
        host = window.location.hostname;
        if (window.location.port) port = `:${window.location.port}`;
      }
    } else {
      host = 'localhost';
      port = ':3001';
    }
    return `${protocol}//${host}${port}/ws/algorithm?token=${token}`;
  };

  const wireSocketHandlers = (websocket) => {
    websocket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const algoId = data.algorithmId || null;

        if (data.type === 'connected' && Array.isArray(data.running) && data.running.length) {
          pushLog(null, 'status', data.message || `${data.running.length} server algo(s) active`);
          return;
        }
        if (data.type === 'attached') {
          if (algoId) {
            attachedIdsRef.current.add(algoId);
            setAttachedIds(new Set(attachedIdsRef.current));
            setAlgorithmId(algoId);
            try { localStorage.setItem('lt-active-algo-id', algoId); } catch { /* ignore */ }
            setLogTab((prev) => prev || algoId);
            if (data.meta?.label) rememberLabel(algoId, data.meta.label);
          }
          pushLog(algoId, 'status', data.message || 'Attached to server algorithm');
          return;
        }
        if (data.type === 'stopped') {
          if (algoId) {
            attachedIdsRef.current.delete(algoId);
            setAttachedIds(new Set(attachedIdsRef.current));
            pushLog(algoId, 'status', data.message || 'Algorithm stopped');
          } else {
            pushLog(null, 'status', data.message || 'Algorithm stopped');
          }
          return;
        }
        if (data.type === 'alert') {
          const detail = data.message || 'Algorithm alert';
          pushLog(algoId, 'error', detail);
          emitEvent({
            title: 'Algorithm halt',
            detail,
            symbol: data.symbol || parsedDesk.symbol || '',
          });
          return;
        }
        if (data.type === 'signal') {
          if (!data.replay) {
            setSignalEvents((prev) => [
              ...prev,
              {
                kind: data.kind || 'signal',
                label: data.label || data.message || data.kind,
                symbol: data.symbol,
                side: data.side,
                qty: data.qty,
                price: data.price,
                timestamp: data.timestamp || new Date().toISOString(),
                algorithmId: algoId,
              },
            ]);
          }
          pushLog(
            algoId,
            data.replay ? 'log' : 'status',
            `${data.replay ? '[replay] ' : ''}Signal: ${data.label || data.kind || 'event'}${data.symbol ? ` (${data.symbol})` : ''}`
          );
          return;
        }
        if (data.type === 'twap_event') {
          pushLog(
            null,
            data.status === 'error' ? 'error' : 'status',
            data.message || `TWAP ${data.kind || 'event'}`
          );
          if (data.job) {
            setTwapJobs((prev) => {
              const others = prev.filter((j) => j.id !== data.job.id);
              return [data.job, ...others];
            });
            setActiveTwapId(data.job.id);
          }
          return;
        }
        let message = data.message || '';
        if (data.type === 'error' && data.stack) message += `\n${data.stack}`;
        const prefix = data.replay ? '[replay] ' : '';
        pushLog(algoId, data.type || 'log', `${prefix}${message}`);
      } catch (error) {
        pushLog(null, 'error', `Failed to parse message: ${error.message}`);
      }
    };

    websocket.onerror = () => {
      pushLog(null, 'warn', 'Log stream disconnected (algorithms keep running on server if started).');
    };

    websocket.onclose = () => {
      wsRef.current = null;
      setWs(null);
      attachedIdsRef.current = new Set();
      setAttachedIds(new Set());
      pushLog(null, 'status', 'Log stream closed. Algorithms continue on server until Stop/Kill.');
    };
  };

  const ensureSocket = async () => {
    if (wsRef.current?.readyState === 1) return wsRef.current;

    let token = accessToken;
    if (!token) {
      pushLog(null, 'error', 'No authentication token found. Please log in again.');
      throw new Error('No auth token');
    }
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        const now = Math.floor(Date.now() / 1000);
        if (payload.exp && payload.exp - now < 300) {
          token = await refreshAuthToken();
        }
      }
    } catch {
      // keep token
    }

    const websocket = new WebSocket(resolveWsUrl(token));
    wireSocketHandlers(websocket);

    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('WebSocket connection timeout'));
        try { websocket.close(); } catch { /* ignore */ }
      }, 10000);
      websocket.onopen = () => {
        clearTimeout(timeout);
        wsRef.current = websocket;
        setWs(websocket);
        resolve();
      };
      websocket.addEventListener('error', () => {
        clearTimeout(timeout);
        reject(new Error('WebSocket connection failed'));
      }, { once: true });
    });
    return websocket;
  };

  const attachToAlgo = async (id, { select = true } = {}) => {
    if (!id) return false;
    try {
      const socket = await ensureSocket();
      if (!attachedIdsRef.current.has(id)) {
        // Fresh attach will replay server backlog — reset local tab to avoid duplicates
        setLogsByAlgo((prev) => ({ ...prev, [id]: [] }));
        socket.send(JSON.stringify({ type: 'attach', algorithmId: id }));
        attachedIdsRef.current.add(id);
        setAttachedIds(new Set(attachedIdsRef.current));
      }
      if (select) {
        setLogTab(id);
        setAlgorithmId(id);
        try { localStorage.setItem('lt-active-algo-id', id); } catch { /* ignore */ }
      }
      return true;
    } catch (err) {
      pushLog(id, 'error', err.message || 'Failed to attach log stream');
      return false;
    }
  };

  // Load Schwab connection + accounts for execution venue picker
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!accessToken) return;
      try {
        setSchwabAccountsLoading(true);
        const statusRes = await schwabApi.getStatus().catch(() => ({ connected: false }));
        if (cancelled) return;
        const connected = Boolean(statusRes?.connected || statusRes?.data?.connected);
        setSchwabConnected(connected);
        if (!connected) {
          setSchwabAccounts([]);
          return;
        }
        const acctRes = await schwabApi.getAccounts(false);
        if (cancelled) return;
        const list = acctRes?.data || acctRes?.accounts || [];
        setSchwabAccounts(Array.isArray(list) ? list : []);
        if (list.length && !list.some((a) => String(a.accountNumber) === String(schwabAccountNumber))) {
          const first = String(list[0].accountNumber || '');
          setSchwabAccountNumber(first);
          try { localStorage.setItem('lt-algo-schwab-account', first); } catch { /* ignore */ }
        }
      } catch (err) {
        if (!cancelled) {
          setSchwabConnected(false);
          setSchwabAccounts([]);
        }
      } finally {
        if (!cancelled) setSchwabAccountsLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, executionVenue]);

  // Reconnect to all server-side scripts still running after page load
  useEffect(() => {
    if (!accessToken) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await algorithmApi.getRunning();
        const scripts = (res.algorithms || []).filter((j) => j.kind === 'script');
        if (cancelled || !scripts.length) return;
        const saved = (() => {
          try { return localStorage.getItem('lt-active-algo-id'); } catch { return null; }
        })();
        const preferred = scripts.find((s) => s.id === saved) || scripts[0];
        setAlgorithmId(preferred.id);
        setLogTab(preferred.id);
        if (preferred.intervalSeconds) setIntervalSeconds(preferred.intervalSeconds);
        pushLog(null, 'status', `Found ${scripts.length} server-side algorithm(s) — attaching log streams…`);
        await ensureSocket();
        for (const s of scripts) {
          if (cancelled) break;
          await attachToAlgo(s.id, { select: s.id === preferred.id });
        }
      } catch (err) {
        console.error('Reconnect failed:', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const handleStart = async () => {
    if (!accessToken) {
      pushLog(null, 'error', 'No authentication token found. Please log in again.');
      return;
    }

    if (!script || script.trim().length === 0) {
      pushLog(null, 'error', 'Script is empty. Please write your algorithm first.');
      return;
    }

    const syntax = runSyntaxCheck();
    if (!syntax.ok) {
      pushLog(null, 'error', 'Fix syntax errors before starting.');
      return;
    }

    if (executionVenue === 'schwab' && !schwabAccountNumber) {
      pushLog(null, 'error', 'Select a Schwab account before starting (or switch venue to Alpaca).');
      return;
    }

    const id = `algo-${Date.now()}`;
    const label =
      algorithmName?.trim() ||
      templates.find((t) => t.id === selectedTemplateId)?.name ||
      parseScriptSymbol(script) ||
      parsedDesk.symbol ||
      id;
    setAlgorithmId(id);
    setLogTab(id);
    setLogsByAlgo((prev) => ({ ...prev, [id]: prev[id] || [] }));
    rememberLabel(id, label);
    try { localStorage.setItem('lt-active-algo-id', id); } catch { /* ignore */ }

    const venueLabel =
      executionVenue === 'schwab'
        ? `Schwab ${schwabAccountNumber}`
        : `Alpaca ${localStorage.getItem('tradingEnv') || 'paper'}`;
    const budget = parseUsdField(runBudgetUsd);
    const halt = parseUsdField(runMaxLossUsd);
    const runRisk = {
      ...riskSettings,
      ...(budget != null
        ? { algoBudgetUsd: budget, runScopedBudget: true, enabled: true }
        : {}),
      ...(halt != null
        ? {
            algoMaxLossUsd: halt,
            algoMaxLossMode: 'usd',
            blockOnAlgoMaxLoss: true,
            runScopedLoss: true,
            enabled: true,
          }
        : {}),
    };
    if (halt != null) {
      requestPermission().catch(() => {});
    }
    pushLog(id, 'status', `Starting “${label}” on SERVER via ${venueLabel} (${id})…`);
    try {
      await algorithmApi.runAlgorithm({
        algorithmId: id,
        script,
        intervalSeconds,
        env: localStorage.getItem('tradingEnv') || 'paper',
        venue: executionVenue,
        accountNumber: executionVenue === 'schwab' ? schwabAccountNumber || null : null,
        tradeOnlyWhenOpen,
        manageExistingPositions,
        riskSettings: runRisk,
        label,
        schedule: buildSchedulePayload(),
        symbols: deskSymbols,
        symbol: parsedDesk.symbol || null,
      });
      pushLog(id, 'status', `Server accepted ${id}. Attaching log stream…`);
      await attachToAlgo(id, { select: true });
      const running = await algorithmApi.getRunning().catch(() => ({ algorithms: [] }));
      setRunningJobs(running.algorithms || []);
    } catch (err) {
      pushLog(id, 'error', err.response?.data?.error || err.message || 'Failed to start algorithm');
    }
  };

  const handleBacktest = async (scriptOverride = null, meta = {}) => {
    if (!accessToken) {
      setBacktestError('Please log in again.');
      return;
    }
    const code = scriptOverride != null ? scriptOverride : script;
    if (!code || !code.trim()) {
      setBacktestError('Script is empty.');
      return;
    }
    const syntax = checkAlgoScriptSyntax(code);
    setSyntaxCheck(syntax);
    if (!syntax.ok) {
      const loc =
        syntax.line != null
          ? ` (line ${syntax.line}${syntax.column != null ? `:${syntax.column}` : ''})`
          : '';
      setBacktestError(`Syntax error${loc}: ${syntax.message}`);
      if (scriptOverride == null && syntax.line != null) {
        codeEditorRef.current?.gotoLine?.(syntax.line, syntax.column || 1);
      }
      return;
    }
    setBacktestRunning(true);
    setBacktestError(null);
    try {
      const symbol =
        parsedDesk.symbol ||
        (backtestSymbol || '').trim().toUpperCase() ||
        parseScriptSymbol(code) ||
        null;
      const res = await algorithmApi.backtest({
        script: code,
        symbol,
        symbols: deskSymbols || symbol,
        timeframe: backtestTimeframe,
        start: backtestStart || null,
        end: backtestEnd || null,
        startTime: isIntradayBacktestTf(backtestTimeframe) ? backtestStartTime || '09:30' : null,
        endTime: isIntradayBacktestTf(backtestTimeframe) ? backtestEndTime || '16:00' : null,
        initialCash: Number(backtestCash) || 100000,
        scalePositions: backtestScalePositions,
        forceRefreshBars: backtestForceRefresh,
        manageExistingPositions,
        env: localStorage.getItem('tradingEnv') || 'paper',
      });
      if (!res.success) {
        throw new Error(res.error || 'Backtest failed');
      }
      setBacktestResult({
        ...res.data,
        label: meta.label || selectedAlgorithm?.name || null,
      });
      if (res.data?.symbol) setChartSymbol(res.data.symbol);
      openIdePanel('backtest');
    } catch (err) {
      setBacktestResult(null);
      setBacktestError(err.response?.data?.error || err.message || 'Backtest failed');
      openIdePanel('backtest');
    } finally {
      setBacktestRunning(false);
    }
  };

  const handleBacktestSavedAlgorithm = async (algo, e) => {
    e?.stopPropagation?.();
    try {
      const response = await algorithmApi.getAlgorithm(algo.id);
      const full = response.data;
      if (!full?.script) {
        setBacktestError('Algorithm has no script');
        openIdePanel('backtest');
        return;
      }
      setScript(full.script);
      setSavedScriptBaseline(full.script || '');
      setIntervalSeconds(full.interval_seconds || 60);
      setRunBudgetUsd(usdFieldFromAlgo(full.run_budget_usd ?? full.runBudgetUsd));
      setRunMaxLossUsd(usdFieldFromAlgo(full.run_max_loss_usd ?? full.runMaxLossUsd));
      setSelectedAlgorithm(full);
      setAlgorithmName(full.name);
      setAlgorithmDescription(full.description || '');
      setSelectedTemplateId('');
      const fromScript = parseScriptSymbolsList(full.script);
      if (fromScript.length) applyDeskSymbols(fromScript.join(', '));
      else {
        const sym = parseScriptSymbol(full.script);
        if (sym) applyDeskSymbols(sym);
      }
      openIdePanel('backtest');
      await handleBacktest(full.script, { label: full.name });
    } catch (err) {
      setBacktestError(err.response?.data?.error || err.message || 'Failed to load algorithm');
      openIdePanel('backtest');
    }
  };

  const handleStop = async (idOverride = null) => {
    const id = idOverride || (logTab !== '__system__' ? logTab : null) || algorithmId;
    if (!id || id === '__system__') {
      pushLog(null, 'warn', 'Select a running algorithm to stop');
      return;
    }
    try {
      await algorithmApi.stopAlgorithm(id);
      pushLog(id, 'status', `Stop requested for ${id}`);
      attachedIdsRef.current.delete(id);
      setAttachedIds(new Set(attachedIdsRef.current));
      const running = await algorithmApi.getRunning().catch(() => ({ algorithms: [] }));
      setRunningJobs(running.algorithms || []);
      const scripts = (running.algorithms || []).filter((j) => j.kind === 'script');
    } catch (err) {
      pushLog(id, 'error', err.response?.data?.error || err.message || 'Stop failed');
    }
  };

  const handleReconnect = async () => {
    try {
      const res = await algorithmApi.getRunning();
      const scripts = (res.algorithms || []).filter((j) => j.kind === 'script');
      if (!scripts.length) {
        pushLog(null, 'warn', 'No server-side algorithms are running');
        return;
      }
      pushLog(null, 'status', `Reconnecting to ${scripts.length} algorithm(s)…`);
      await ensureSocket();
      for (const s of scripts) {
        await attachToAlgo(s.id, { select: false });
      }
      const preferred = scripts.find((s) => s.id === algorithmId) || scripts[0];
      setAlgorithmId(preferred.id);
      setLogTab(preferred.id);
    } catch (err) {
      pushLog(null, 'error', err.message || 'Reconnect failed');
    }
  };

  const clearLogs = () => {
    if (logTab === '__system__' || !logTab) {
      setSystemLogs([]);
      return;
    }
    setLogsByAlgo((prev) => ({ ...prev, [logTab]: [] }));
  };

  const selectLogTab = (id) => {
    setLogTab(id);
    if (id && id !== '__system__') {
      setAlgorithmId(id);
      try { localStorage.setItem('lt-active-algo-id', id); } catch { /* ignore */ }
      if (scriptJobs.some((j) => j.id === id) && !attachedIdsRef.current.has(id)) {
        attachToAlgo(id, { select: true });
      }
    }
  };

  const closeLogTab = (id, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!id || id === '__system__') return;
    if (scriptJobs.some((j) => j.id === id)) return;
    setLogsByAlgo((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setLogTab((prev) => (prev === id ? '__system__' : prev));
    if (algorithmId === id) {
      const leftover = scriptJobs.find((j) => j.id !== id);
      if (leftover?.id) setAlgorithmId(leftover.id);
    }
  };

  const formatTimestamp = (timestamp) => {
    // Live Output tab — show clock in ET
    return formatEtDateTime(timestamp, { seconds: true }).slice(11) || new Date(timestamp).toLocaleTimeString();
  };


  return (
    <main
      className={`flex-1 ${
        idePopout
          ? 'w-full max-w-none px-0 py-0'
          : 'container mx-auto px-4 lg:px-6 py-3'
      }`}
    >
      {!idePopout && (
      <div className="mb-3">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faCode} className="mr-2 text-indigo-600" />
          Algorithmic Trading
        </h1>
        <p className="text-xs text-gray-500 mt-1 max-w-3xl">
          Server-side multi-algo desk — scripts keep running after you close the browser. Backtest and Chart live under the editor (Output / Backtest / Chart tabs). Use fullscreen or pop-out for a dedicated coding window.
        </p>
        {scriptJobs.length > 0 && (
          <div className="mt-2 text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 rounded px-2 py-1.5 inline-flex flex-wrap items-center gap-2">
            <span>
              {scriptJobs.length} script{scriptJobs.length === 1 ? '' : 's'} running
              {attachedIds.size ? ` · ${attachedIds.size} log stream${attachedIds.size === 1 ? '' : 's'} attached` : ' · logs detached'}
            </span>
            {attachedIds.size < scriptJobs.length && (
              <button
                type="button"
                onClick={handleReconnect}
                className="px-2 py-0.5 bg-indigo-600 text-white rounded text-[10px] font-medium"
              >
                Attach all logs
              </button>
            )}
          </div>
        )}
      </div>
      )}

      {/* Algo budget & max loss */}
      {!idePopout && (
      <div className="mb-3 bg-white rounded-lg shadow-md border border-gray-100 p-3">
        <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
          <div>
            <h3 className="text-sm font-bold text-gray-800">Risk limits</h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Desk-wide defaults for algo buys. The editor Use $ / Halt $ fields override these for that run.
            </p>
          </div>
          <div className="text-right text-[11px]">
            <div className="text-gray-500">Today P/L</div>
            <div
              className={`font-semibold ${
                dayPl == null ? 'text-gray-400' : dayPl >= 0 ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {dayPl == null
                ? '—'
                : `${dayPl >= 0 ? '+' : '-'}$${Math.abs(dayPl).toFixed(2)}`}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
              Budget (USD)
            </label>
            <input
              type="number"
              min="0"
              step="100"
              value={riskSettings.algoBudgetUsd ?? ''}
              onChange={(e) =>
                updateRiskSettings({
                  algoBudgetUsd: e.target.value === '' ? null : Number(e.target.value),
                })
              }
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg"
              placeholder="e.g. 5000"
            />
            <p className="text-[10px] text-gray-500 mt-1">
              Max $ per algo order / risk sizing. Equity: $
              {account?.equity != null ? Number(account.equity).toFixed(0) : '—'}
            </p>
          </div>

          <div className="md:col-span-2">
            <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">
              Max I can afford to lose
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-xs">
                <button
                  type="button"
                  onClick={() => updateRiskSettings({ algoMaxLossMode: 'usd' })}
                  className={`px-2.5 py-1.5 ${
                    riskSettings.algoMaxLossMode !== 'pct'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  $
                </button>
                <button
                  type="button"
                  onClick={() => updateRiskSettings({ algoMaxLossMode: 'pct' })}
                  className={`px-2.5 py-1.5 border-l border-gray-300 ${
                    riskSettings.algoMaxLossMode === 'pct'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  %
                </button>
              </div>

              {riskSettings.algoMaxLossMode === 'pct' ? (
                <>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={riskSettings.algoMaxLossPct ?? ''}
                    onChange={(e) =>
                      updateRiskSettings({
                        algoMaxLossPct: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                    className="w-24 px-2 py-1.5 text-xs border border-gray-300 rounded-lg"
                    placeholder="e.g. 2"
                  />
                  <span className="text-xs text-gray-600">% of</span>
                  <select
                    value={riskSettings.algoMaxLossBasis || 'equity'}
                    onChange={(e) => updateRiskSettings({ algoMaxLossBasis: e.target.value })}
                    className="px-2 py-1.5 text-xs border border-gray-300 rounded-lg"
                  >
                    <option value="equity">account equity</option>
                    <option value="budget">algo budget</option>
                  </select>
                </>
              ) : (
                <input
                  type="number"
                  min="0"
                  step="50"
                  value={riskSettings.algoMaxLossUsd ?? ''}
                  onChange={(e) =>
                    updateRiskSettings({
                      algoMaxLossUsd: e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                  className="w-32 px-2 py-1.5 text-xs border border-gray-300 rounded-lg"
                  placeholder="e.g. 250"
                />
              )}

              <label className="flex items-center gap-1.5 text-xs text-gray-700 ml-auto">
                <input
                  type="checkbox"
                  checked={riskSettings.blockOnAlgoMaxLoss !== false}
                  onChange={(e) => updateRiskSettings({ blockOnAlgoMaxLoss: e.target.checked })}
                  className="rounded border-gray-300"
                />
                Halt new buys at limit
              </label>
            </div>
            <p className="text-[10px] text-gray-500 mt-1">
              Effective max loss:{' '}
              <span className="font-semibold text-gray-700">
                {algoMaxLossUsd != null ? `$${algoMaxLossUsd.toFixed(2)}` : 'not set'}
              </span>
              {algoMaxLossUsd != null && dayPl != null && (
                <>
                  {' '}
                  · room left:{' '}
                  <span
                    className={
                      algoMaxLossUsd + dayPl <= 0 ? 'text-red-600 font-semibold' : 'text-gray-700'
                    }
                  >
                    ${Math.max(0, algoMaxLossUsd + dayPl).toFixed(2)}
                  </span>
                </>
              )}
            </p>
          </div>
        </div>
      </div>
      )}


      {/* Workspace tabs */}
      <div className={`mb-3 flex flex-wrap items-center gap-1 border-b border-gray-200 pb-2 ${idePopout ? 'hidden' : ''}`}>
        {[
          { id: 'workbench', label: 'Workbench', icon: faCode },
          { id: 'twap', label: 'TWAP', icon: faClock },
          { id: 'reference', label: 'Guides', icon: faBook },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setWorkspace(tab.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 ${
              workspace === tab.id ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <FontAwesomeIcon icon={tab.icon} className="text-[10px]" />
            {tab.label}
            {tab.id === 'twap' && twapJobs.length > 0 ? (
              <span className={`text-[9px] px-1 rounded ${workspace === tab.id ? 'bg-white/20' : 'bg-amber-100 text-amber-800'}`}>{twapJobs.length}</span>
            ) : null}
            {tab.id === 'workbench' && scriptJobs.length > 0 ? (
              <span className={`text-[9px] px-1 rounded ${workspace === tab.id ? 'bg-white/20' : 'bg-indigo-100 text-indigo-800'}`}>{scriptJobs.length}</span>
            ) : null}
            {tab.id === 'workbench' && backtestResult ? (
              <span className={`text-[9px] px-1 rounded ${workspace === tab.id ? 'bg-white/20' : 'bg-teal-100 text-teal-700'}`}>bt</span>
            ) : null}
          </button>
        ))}
        <div className="ml-auto flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => handleKillAll(false)}
            className="px-2.5 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-[11px] font-medium inline-flex items-center gap-1"
            title="Stop scripts/TWAP; cancel open orders on algo-tracked symbols only"
          >
            <FontAwesomeIcon icon={faSkullCrossbones} />
            Kill algos
          </button>
          <button
            type="button"
            onClick={() => handleKillAll(true)}
            className="px-2.5 py-1.5 bg-red-700 text-white rounded-lg hover:bg-red-800 text-[11px] font-medium"
            title="Also close quantities opened by algorithms — not your whole portfolio"
          >
            Kill + close
          </button>
        </div>
      </div>


      {workspace === 'workbench' && (
        <>
      {/* Controls: templates, hours, kill */}
      {!idePopout && (
      <>
      <div className="mb-3 bg-white rounded-lg shadow-md border border-gray-100 p-3 flex flex-wrap items-end gap-3">
        <label
          className="flex items-center gap-2 text-xs text-gray-700 pb-1.5 cursor-pointer"
          title="Uncheck to let scripts and TWAP run when the session is closed (paper / after-hours testing). Keep on for live-session safety."
        >
          <input
            type="checkbox"
            checked={tradeOnlyWhenOpen}
            onChange={(e) => {
              const on = e.target.checked;
              setTradeOnlyWhenOpen(on);
              try {
                localStorage.setItem('lt-algo-trade-only-when-open', on ? '1' : '0');
              } catch { /* ignore */ }
            }}
            className="rounded border-gray-300"
          />
          <FontAwesomeIcon icon={faClock} className="text-gray-500" />
          Trade only when market open
          <span className="text-[10px] text-gray-400 font-normal">(uncheck to test after hours)</span>
        </label>
        <label
          className="flex items-center gap-2 text-xs text-gray-700 pb-1.5 cursor-pointer"
          title="Let exit signals act on holdings you already own for the script symbol; also tracks them for Kill + close"
        >
          <input
            type="checkbox"
            checked={manageExistingPositions}
            onChange={(e) => setManageExistingPositions(e.target.checked)}
            className="rounded border-gray-300"
          />
          Manage existing positions
        </label>
        <button
          type="button"
          onClick={() => setShowSchedulePanel((v) => !v)}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
            showSchedulePanel || skipOpenClose || useCustomHours || scheduleDateFrom || scheduleDateTo
              ? 'bg-indigo-50 text-indigo-800 border-indigo-200'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
          }`}
          title="Skip open/close windows or set custom hours and dates (US/Eastern)"
        >
          <FontAwesomeIcon icon={faClock} className="mr-1" />
          Run schedule
        </button>
        <button
          type="button"
          onClick={() => {
            if (parsedDesk.symbol) setBacktestSymbol(parsedDesk.symbol);
            openIdePanel('backtest');
          }}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
            ideBottomTab === 'backtest' || backtestResult
              ? 'bg-teal-50 text-teal-800 border-teal-200'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
          }`}
          title="Open Backtest panel in the editor"
        >
          <FontAwesomeIcon icon={faFlask} className="mr-1" />
          Backtest
        </button>
        <button
          type="button"
          onClick={() => {
            if (parsedDesk.symbol) setChartSymbol(parsedDesk.symbol);
            openIdePanel('chart');
          }}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
            ideBottomTab === 'chart'
              ? 'bg-indigo-50 text-indigo-800 border-indigo-200'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
          }`}
          title="Open Chart panel in the editor"
        >
          <FontAwesomeIcon icon={faChartLine} className="mr-1" />
          Chart
        </button>
      </div>

      {showSchedulePanel && (
        <div className="mb-3 bg-white rounded-lg shadow-md border border-indigo-100 p-3 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h3 className="text-sm font-bold text-gray-800">When this algorithm may run</h3>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Times use US/Eastern. Applied on every tick before your script runs (logged when skipped).
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-lg border border-gray-200 p-2.5 space-y-2">
              <label className="flex items-center gap-2 text-xs text-gray-800 font-medium cursor-pointer">
                <input
                  type="checkbox"
                  checked={skipOpenClose}
                  onChange={(e) => setSkipOpenClose(e.target.checked)}
                  className="rounded border-gray-300"
                />
                Skip open &amp; close windows
              </label>
              <p className="text-[10px] text-gray-500">
                Avoid the first/last minutes of the regular session (09:30–16:00 ET).
              </p>
              <div className="flex flex-wrap gap-3 items-end">
                <label className="text-[10px] text-gray-600">
                  Skip first (min)
                  <input
                    type="number"
                    min="0"
                    max="120"
                    disabled={!skipOpenClose}
                    value={skipOpenMinutes}
                    onChange={(e) => setSkipOpenMinutes(Math.max(0, parseInt(e.target.value, 10) || 0))}
                    className="mt-0.5 block w-20 px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-50"
                  />
                </label>
                <label className="text-[10px] text-gray-600">
                  Skip last (min)
                  <input
                    type="number"
                    min="0"
                    max="120"
                    disabled={!skipOpenClose}
                    value={skipCloseMinutes}
                    onChange={(e) => setSkipCloseMinutes(Math.max(0, parseInt(e.target.value, 10) || 0))}
                    className="mt-0.5 block w-20 px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-50"
                  />
                </label>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 p-2.5 space-y-2">
              <label className="flex items-center gap-2 text-xs text-gray-800 font-medium cursor-pointer">
                <input
                  type="checkbox"
                  checked={useCustomHours}
                  onChange={(e) => setUseCustomHours(e.target.checked)}
                  className="rounded border-gray-300"
                />
                Custom hours (ET)
              </label>
              <div className="flex flex-wrap gap-3 items-end">
                <label className="text-[10px] text-gray-600">
                  From
                  <input
                    type="time"
                    disabled={!useCustomHours}
                    value={scheduleStartTime}
                    onChange={(e) => setScheduleStartTime(e.target.value)}
                    className="mt-0.5 block px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-50"
                  />
                </label>
                <label className="text-[10px] text-gray-600">
                  To
                  <input
                    type="time"
                    disabled={!useCustomHours}
                    value={scheduleEndTime}
                    onChange={(e) => setScheduleEndTime(e.target.value)}
                    className="mt-0.5 block px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-50"
                  />
                </label>
              </div>
              <div className="flex flex-wrap gap-1 pt-1">
                {[
                  { d: 1, l: 'Mon' },
                  { d: 2, l: 'Tue' },
                  { d: 3, l: 'Wed' },
                  { d: 4, l: 'Thu' },
                  { d: 5, l: 'Fri' },
                  { d: 6, l: 'Sat' },
                  { d: 0, l: 'Sun' },
                ].map(({ d, l }) => (
                  <button
                    key={d}
                    type="button"
                    disabled={!useCustomHours}
                    onClick={() => toggleScheduleDay(d)}
                    className={`px-2 py-0.5 rounded text-[10px] font-medium border disabled:opacity-40 ${
                      scheduleDays.includes(d)
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-gray-600 border-gray-300'
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 items-end border-t border-gray-100 pt-3">
            <label className="text-[10px] text-gray-600">
              Only from date (optional)
              <input
                type="date"
                value={scheduleDateFrom}
                onChange={(e) => setScheduleDateFrom(e.target.value)}
                className="mt-0.5 block px-2 py-1 text-xs border border-gray-300 rounded"
              />
            </label>
            <label className="text-[10px] text-gray-600">
              Only until date (optional)
              <input
                type="date"
                value={scheduleDateTo}
                onChange={(e) => setScheduleDateTo(e.target.value)}
                className="mt-0.5 block px-2 py-1 text-xs border border-gray-300 rounded"
              />
            </label>
            <button
              type="button"
              onClick={() => {
                setSkipOpenClose(false);
                setSkipOpenMinutes(15);
                setSkipCloseMinutes(15);
                setUseCustomHours(false);
                setScheduleStartTime('09:45');
                setScheduleEndTime('15:45');
                setScheduleDays([1, 2, 3, 4, 5]);
                setScheduleDateFrom('');
                setScheduleDateTo('');
              }}
              className="px-2 py-1 text-[10px] text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
            >
              Reset schedule
            </button>
          </div>
        </div>
      )}
      </>
      )}


      {/* IDE workbench: explorer + editor + docs */}
      <div
        ref={ideShellRef}
        data-ide-theme={ideTheme}
        className={`algo-ide-shell ${
          idePopout
            ? 'h-screen min-h-screen rounded-none border-0'
            : ideFullscreen
              ? 'fixed inset-0 z-[80] m-0 rounded-none border-0 shadow-2xl'
              : 'mb-3 rounded-lg border shadow-sm'
        } overflow-hidden ${
          ideDark
            ? 'border-slate-700 bg-[var(--ide-editor)]'
            : 'border-gray-200 bg-white'
        }`}
      >
        <div
          className={`flex flex-col lg:flex-row ${
            ideFullscreen || idePopout ? 'h-full' : 'lg:h-[min(82vh,920px)] lg:min-h-[560px]'
          }`}
        >
          {/* Explorer */}
          {!explorerOpen ? (
            <div
              className={`hidden lg:flex w-9 shrink-0 flex-col items-center border-r py-2 gap-2 ${
                ideDark ? 'border-white/10 bg-[var(--ide-sidebar)]' : 'border-gray-200 bg-gray-50'
              }`}
            >
              <button
                type="button"
                onClick={() => setExplorerOpen(true)}
                className={`h-7 w-7 rounded inline-flex items-center justify-center ${
                  ideDark ? 'text-slate-300 hover:bg-white/10' : 'text-gray-600 hover:bg-gray-200'
                }`}
                title="Show explorer"
              >
                <FontAwesomeIcon icon={faAnglesRight} className="text-[11px]" />
              </button>
              <button
                type="button"
                onClick={() => setExplorerOpen(true)}
                className={`h-7 w-7 rounded inline-flex items-center justify-center ${
                  ideDark ? 'text-slate-400 hover:bg-white/10' : 'text-gray-500 hover:bg-gray-200'
                }`}
                title="Explorer"
              >
                <FontAwesomeIcon icon={faFolder} className="text-[11px]" />
              </button>
            </div>
          ) : (
          <aside
            className={`w-full lg:w-52 xl:w-56 shrink-0 flex flex-col border-b lg:border-b-0 lg:border-r max-h-52 lg:max-h-none lg:h-full ${
              ideDark ? 'border-white/10 bg-[var(--ide-sidebar)]' : 'border-gray-200 bg-gray-50'
            }`}
          >
            <div className={`h-9 px-2.5 flex items-center justify-between gap-2 border-b ${ideDark ? 'border-white/10' : 'border-gray-200'}`}>
              <div className={`text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1.5 ${ideDark ? 'text-slate-400' : 'text-gray-500'}`}>
                <FontAwesomeIcon icon={faFolder} className="text-[10px]" />
                Explorer
              </div>
              <div className="flex items-center gap-0.5">
                <button
                  type="button"
                  onClick={handleNewAlgorithm}
                  className={`h-6 px-1.5 rounded text-[10px] font-medium inline-flex items-center gap-1 ${
                    ideDark ? 'text-indigo-300 hover:bg-white/10' : 'text-indigo-600 hover:bg-indigo-50'
                  }`}
                  title="New algorithm"
                >
                  <FontAwesomeIcon icon={faPlus} className="text-[9px]" />
                  New
                </button>
                <button
                  type="button"
                  onClick={() => setExplorerOpen(false)}
                  className={`h-6 w-6 rounded inline-flex items-center justify-center ${
                    ideDark ? 'text-slate-400 hover:bg-white/10 hover:text-slate-200' : 'text-gray-500 hover:bg-gray-200 hover:text-gray-800'
                  }`}
                  title="Collapse explorer"
                >
                  <FontAwesomeIcon icon={faAnglesLeft} className="text-[10px]" />
                </button>
              </div>
            </div>
            <div className={`px-2 py-1.5 border-b ${ideDark ? 'border-white/5' : 'border-gray-100'}`}>
              <input
                value={algoExplorerQuery}
                onChange={(e) => setAlgoExplorerQuery(e.target.value)}
                placeholder="Filter templates & scripts…"
                className={`w-full h-7 px-2 rounded text-[11px] focus:outline-none ${
                  ideDark
                    ? 'bg-black/30 border border-white/10 text-slate-200 placeholder:text-slate-500 focus:border-indigo-400/50'
                    : 'bg-white border border-gray-200 text-gray-800 placeholder:text-gray-400 focus:border-indigo-400'
                }`}
              />
            </div>
            <div className="flex-1 overflow-y-auto p-1 space-y-2">
              <div>
                <button
                  type="button"
                  onClick={() => setTemplatesOpen((v) => !v)}
                  className={`w-full px-2 py-1 flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-wider rounded ${
                    ideDark
                      ? 'text-slate-500 hover:bg-white/5 hover:text-slate-300'
                      : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                  }`}
                  title={templatesOpen ? 'Collapse templates' : 'Expand templates'}
                >
                  <FontAwesomeIcon
                    icon={templatesOpen ? faChevronDown : faChevronRight}
                    className="text-[8px] w-2.5"
                  />
                  Templates
                  <span className={`ml-auto normal-case tracking-normal font-medium ${ideDark ? 'text-slate-600' : 'text-gray-400'}`}>
                    {filteredTemplates.length}
                  </span>
                </button>
                {templatesOpen ? (
                  filteredTemplates.length === 0 ? (
                    <div className={`px-2 py-2 text-[11px] ${ideDark ? 'text-slate-500' : 'text-gray-400'}`}>
                      No templates match.
                    </div>
                  ) : (
                    <div className="space-y-0.5">
                      {filteredTemplates.map((t) => {
                        const active = selectedTemplateId === t.id && !selectedAlgorithm;
                        return (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => handleLoadTemplate(t.id)}
                            className={`w-full text-left group flex items-center gap-1.5 rounded px-2 py-1 ${
                              active
                                ? ideDark
                                  ? 'bg-teal-500/20 text-teal-100'
                                  : 'bg-teal-50 text-teal-900'
                                : ideDark
                                  ? 'text-slate-300 hover:bg-white/5'
                                  : 'text-gray-700 hover:bg-gray-100'
                            }`}
                            title={t.description || t.name}
                          >
                            <FontAwesomeIcon
                              icon={faFlask}
                              className={`text-[9px] shrink-0 ${
                                active
                                  ? ideDark ? 'text-teal-300' : 'text-teal-600'
                                  : ideDark ? 'text-slate-500' : 'text-gray-400'
                              }`}
                            />
                            <div className="min-w-0 flex-1">
                              <div className="text-[11px] font-medium truncate leading-tight">{t.name}</div>
                              <div className={`text-[9px] truncate leading-tight ${ideDark ? 'text-slate-500' : 'text-gray-400'}`}>
                                {t.category || 'strategy'}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )
                ) : null}
              </div>

              <div>
                <div className={`px-2 py-1 text-[9px] font-semibold uppercase tracking-wider ${ideDark ? 'text-slate-500' : 'text-gray-400'}`}>
                  Saved scripts
                </div>
              {loadingAlgorithms ? (
                <div className={`text-center py-6 text-[11px] ${ideDark ? 'text-slate-500' : 'text-gray-400'}`}>
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin mb-2" />
                  <div>Loading…</div>
                </div>
              ) : filteredAlgorithms.length === 0 ? (
                <div className={`text-center py-5 px-2 text-[11px] space-y-2 ${ideDark ? 'text-slate-500' : 'text-gray-400'}`}>
                  <p>{algorithms.length === 0 ? 'No saved scripts yet.' : 'No matches.'}</p>
                  {algorithms.length === 0 && (
                    <button
                      type="button"
                      onClick={handleNewAlgorithm}
                      className="px-2 py-1 rounded bg-indigo-600 text-white text-[10px] font-medium"
                    >
                      Create first script
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-0.5">
                {filteredAlgorithms.map((algo) => {
                  const active = selectedAlgorithm?.id === algo.id;
                  return (
                    <div
                      key={algo.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleLoadAlgorithm(algo)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleLoadAlgorithm(algo);
                        }
                      }}
                      className={`group flex items-center gap-1.5 rounded px-2 py-1 cursor-pointer ${
                        active
                          ? ideDark
                            ? 'bg-indigo-500/25 text-indigo-100'
                            : 'bg-indigo-100 text-indigo-900'
                          : ideDark
                            ? 'text-slate-300 hover:bg-white/5'
                            : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <FontAwesomeIcon
                        icon={faFileCode}
                        className={`text-[10px] shrink-0 ${
                          active
                            ? ideDark ? 'text-indigo-300' : 'text-indigo-600'
                            : ideDark ? 'text-slate-500' : 'text-gray-400'
                        }`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-[11px] font-medium truncate leading-tight">{algo.name}</div>
                        <div className={`text-[9px] truncate leading-tight ${ideDark ? 'text-slate-500' : 'text-gray-400'}`}>
                          {algo.interval_seconds || 60}s
                          {algo.description ? ` · ${algo.description}` : ''}
                        </div>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 shrink-0">
                        <button
                          type="button"
                          onClick={(e) => handleBacktestSavedAlgorithm(algo, e)}
                          disabled={backtestRunning}
                          className={`p-1 rounded disabled:opacity-40 ${ideDark ? 'text-teal-300 hover:bg-white/10' : 'text-teal-600 hover:bg-teal-50'}`}
                          title="Backtest"
                        >
                          <FontAwesomeIcon icon={faFlask} className="text-[9px]" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteAlgorithm(algo);
                          }}
                          className={`p-1 rounded ${ideDark ? 'text-red-300 hover:bg-white/10' : 'text-red-500 hover:bg-red-50'}`}
                          title="Delete"
                        >
                          <FontAwesomeIcon icon={faTrash} className="text-[9px]" />
                        </button>
                      </div>
                    </div>
                  );
                })}
                </div>
              )}
              </div>
            </div>
          </aside>
          )}

          {/* Editor pane */}
          <div
            ref={editorPaneRef}
            className={`flex-1 min-w-0 flex flex-col min-h-0 ${ideDark ? 'bg-[var(--ide-editor)]' : 'bg-white'}`}
          >
            {/* IDE toolbar: file/run on top, stretching fields below */}
            <div
              className={`shrink-0 border-b ${
                ideDark ? 'border-white/10 bg-[var(--ide-toolbar)]' : 'border-gray-200 bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-2 px-2 h-9">
                {!explorerOpen && (
                  <button
                    type="button"
                    onClick={() => setExplorerOpen(true)}
                    className={`lg:hidden h-7 w-7 rounded inline-flex items-center justify-center shrink-0 ${
                      ideDark ? 'text-slate-300 hover:bg-white/10' : 'text-gray-600 hover:bg-gray-200'
                    }`}
                    title="Show explorer"
                  >
                    <FontAwesomeIcon icon={faFolder} className="text-[11px]" />
                  </button>
                )}
                <div
                  className={`min-w-0 flex-1 inline-flex items-center gap-1.5 h-7 px-2.5 rounded text-[11px] font-medium ${
                    scriptDirty
                      ? ideDark
                        ? 'bg-[var(--ide-editor)] text-amber-100 ring-1 ring-amber-400/40'
                        : 'bg-white text-amber-800 ring-1 ring-amber-300'
                      : ideDark
                        ? 'bg-[var(--ide-editor)] text-slate-200 ring-1 ring-white/10'
                        : 'bg-white text-gray-800 ring-1 ring-gray-200'
                  }`}
                >
                  <FontAwesomeIcon icon={faFileCode} className={`text-[10px] shrink-0 ${ideDark ? 'text-indigo-300' : 'text-indigo-500'}`} />
                  <span className="truncate">
                    {selectedAlgorithm?.name || algorithmName || 'untitled.js'}
                  </span>
                  {scriptDirty ? <span className="text-amber-500 shrink-0" title="Unsaved changes">●</span> : null}
                </div>
                <button
                  type="button"
                  onClick={() => setShowSaveModal(true)}
                  className="h-7 px-2.5 rounded bg-indigo-600 hover:bg-indigo-500 text-white text-[11px] font-semibold inline-flex items-center gap-1 shrink-0"
                >
                  <FontAwesomeIcon icon={faSave} className="text-[10px]" />
                  {selectedAlgorithm ? 'Save' : 'Save as…'}
                  {scriptDirty ? ' *' : ''}
                </button>
                <span className={`w-px h-4 shrink-0 ${ideDark ? 'bg-white/10' : 'bg-gray-200'}`} />
                <button
                  type="button"
                  onClick={runSyntaxCheck}
                  className={`h-7 px-2.5 rounded text-[11px] font-semibold inline-flex items-center gap-1 shrink-0 ${
                    syntaxCheck.ok
                      ? ideDark
                        ? 'border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10'
                        : 'border border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                      : ideDark
                        ? 'border border-red-500/40 text-red-300 hover:bg-red-500/10'
                        : 'border border-red-300 text-red-700 hover:bg-red-50'
                  }`}
                  title="Check that the script compiles (same as the server runner)"
                >
                  <FontAwesomeIcon icon={syntaxCheck.ok ? faCheck : faExclamationTriangle} className="text-[10px]" />
                  Check
                </button>
                <button
                  type="button"
                  onClick={handleStart}
                  disabled={!syntaxCheck.ok}
                  className="h-7 px-3 rounded bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-[11px] font-semibold inline-flex items-center gap-1 shrink-0"
                  title={syntaxCheck.ok ? (scriptJobs.length ? 'Start another server-side algorithm' : 'Start') : 'Fix syntax errors before starting'}
                >
                  <FontAwesomeIcon icon={faPlay} className="text-[10px]" />
                  Start
                </button>
                {scriptJobs.length > 0 && attachedIds.size < scriptJobs.length && (
                  <button
                    type="button"
                    onClick={handleReconnect}
                    className="h-7 px-2.5 rounded bg-indigo-700 hover:bg-indigo-600 text-white text-[11px] font-medium shrink-0"
                  >
                    Attach logs
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleStop()}
                  disabled={!scriptJobs.some((j) => j.id === logTab || j.id === algorithmId)}
                  className="h-7 px-2.5 rounded bg-red-600 hover:bg-red-500 disabled:opacity-40 text-white text-[11px] font-semibold inline-flex items-center gap-1 shrink-0"
                >
                  <FontAwesomeIcon icon={faStop} className="text-[10px]" />
                  Stop
                </button>
              </div>

              <div className={`flex items-center gap-1.5 px-2 h-9 border-t overflow-x-auto ${ideDark ? 'border-white/10' : 'border-gray-200'}`}>
                <label
                  className={`h-7 px-2 inline-flex items-center gap-1.5 rounded text-[10px] flex-[1.2] min-w-[9rem] ${
                    ideDark
                      ? 'border border-amber-400/40 text-amber-200 bg-amber-500/10'
                      : 'border border-amber-300 text-amber-900 bg-amber-50'
                  }`}
                  title="Primary ticker is injected as `symbol`; additional names as `symbols` (and symbolY / symbolX for pairs). Do not hardcode tickers in the script."
                >
                  Symbols
                  <input
                    value={deskSymbols}
                    onChange={(e) => applyDeskSymbols(e.target.value)}
                    placeholder="AAPL, MSFT"
                    className={`bg-transparent text-[11px] focus:outline-none flex-1 min-w-0 uppercase tracking-wide ${
                      ideDark ? 'text-slate-100 placeholder:text-slate-500' : 'text-gray-800 placeholder:text-gray-400'
                    }`}
                  />
                </label>
                <label
                  className={`h-7 px-2 inline-flex items-center gap-1.5 rounded text-[10px] flex-1 min-w-[8rem] ${
                    ideDark
                      ? 'border border-white/10 text-slate-400 bg-[var(--ide-editor)]'
                      : 'border border-gray-200 text-gray-500 bg-white'
                  }`}
                  title="Load a strategy template into the editor"
                >
                  Template
                  <select
                    value={selectedTemplateId}
                    onChange={(e) => handleLoadTemplate(e.target.value)}
                    className={`bg-transparent text-[11px] focus:outline-none flex-1 min-w-0 ${
                      ideDark ? 'text-slate-200' : 'text-gray-800'
                    }`}
                  >
                    <option value="">Custom / saved…</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}{t.category ? ` · ${t.category}` : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <div
                  className={`h-7 inline-flex items-center rounded text-[10px] shrink-0 overflow-hidden ${
                    ideDark
                      ? 'border border-white/10 text-slate-400 bg-[var(--ide-editor)]'
                      : 'border border-gray-200 text-gray-500 bg-white'
                  }`}
                >
                  <select
                    value={executionVenue}
                    onChange={(e) => {
                      const next = e.target.value === 'schwab' ? 'schwab' : 'alpaca';
                      setExecutionVenue(next);
                      try { localStorage.setItem('lt-algo-venue', next); } catch { /* ignore */ }
                    }}
                    className={`h-full px-2 bg-transparent text-[11px] focus:outline-none ${
                      ideDark ? 'text-slate-200' : 'text-gray-800'
                    }`}
                    title="Where algo orders are sent"
                  >
                    <option value="alpaca">Alpaca</option>
                    <option value="schwab">Schwab</option>
                  </select>
                  {executionVenue === 'schwab' && (
                    <select
                      value={schwabAccountNumber}
                      onChange={(e) => {
                        const next = e.target.value;
                        setSchwabAccountNumber(next);
                        try { localStorage.setItem('lt-algo-schwab-account', next); } catch { /* ignore */ }
                      }}
                      disabled={schwabAccountsLoading || !schwabConnected || schwabAccounts.length === 0}
                      className={`h-full px-2 bg-transparent text-[11px] focus:outline-none border-l max-w-[7.5rem] ${
                        ideDark
                          ? 'border-white/10 text-amber-200'
                          : 'border-gray-200 text-amber-900'
                      }`}
                      title={
                        !schwabConnected
                          ? 'Connect Schwab on the Schwab page first'
                          : 'Schwab account for live algo orders'
                      }
                    >
                      {!schwabConnected && <option value="">Not connected</option>}
                      {schwabConnected && schwabAccounts.length === 0 && (
                        <option value="">{schwabAccountsLoading ? 'Loading…' : 'No accounts'}</option>
                      )}
                      {schwabAccounts.map((a) => (
                        <option key={a.accountNumber} value={a.accountNumber}>
                          {(a.accountType || 'acct')} …{String(a.accountNumber || '').slice(-4)}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div
                  className={`h-7 inline-flex items-stretch rounded text-[10px] shrink-0 overflow-hidden ${
                    ideDark
                      ? 'border border-white/10 text-slate-400 bg-[var(--ide-editor)]'
                      : 'border border-gray-200 text-gray-500 bg-white'
                  }`}
                >
                  <label
                    className="px-1.5 inline-flex items-center gap-1"
                    title="Capital this algorithm run may deploy (open notional). Empty = desk default."
                  >
                    Use
                    <input
                      type="number"
                      min="0"
                      step="100"
                      value={runBudgetUsd}
                      onChange={(e) => setRunBudgetUsd(e.target.value)}
                      placeholder="—"
                      className={`w-14 bg-transparent text-[11px] focus:outline-none ${ideDark ? 'text-slate-200' : 'text-gray-800'}`}
                    />
                  </label>
                  <label
                    className={`px-1.5 inline-flex items-center gap-1 border-l ${ideDark ? 'border-white/10' : 'border-gray-200'}`}
                    title="Stop this run and alert if its P/L reaches −this amount. Empty = no per-run halt."
                  >
                    Halt
                    <input
                      type="number"
                      min="0"
                      step="50"
                      value={runMaxLossUsd}
                      onChange={(e) => setRunMaxLossUsd(e.target.value)}
                      placeholder="—"
                      className={`w-14 bg-transparent text-[11px] focus:outline-none ${ideDark ? 'text-slate-200' : 'text-gray-800'}`}
                    />
                  </label>
                  <label
                    className={`px-1.5 inline-flex items-center gap-1 border-l ${ideDark ? 'border-white/10' : 'border-gray-200'}`}
                    title="Tick interval in seconds"
                  >
                    <input
                      type="number"
                      value={intervalSeconds}
                      onChange={(e) => setIntervalSeconds(Math.max(10, parseInt(e.target.value, 10) || 60))}
                      min="10"
                      className={`w-9 bg-transparent text-[11px] focus:outline-none ${ideDark ? 'text-slate-200' : 'text-gray-800'}`}
                    />
                    s
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (parsedDesk.symbol) setBacktestSymbol(parsedDesk.symbol);
                    openIdePanel('backtest');
                    handleBacktest(null, {
                      label: selectedAlgorithm?.name || algorithmName || 'Editor script',
                    });
                  }}
                  disabled={backtestRunning || !syntaxCheck.ok}
                  className="h-7 px-2.5 rounded bg-teal-700 hover:bg-teal-600 disabled:opacity-50 text-white text-[11px] font-semibold inline-flex items-center gap-1 shrink-0"
                >
                  <FontAwesomeIcon icon={backtestRunning ? faSpinner : faFlask} className={`text-[10px] ${backtestRunning ? 'animate-spin' : ''}`} />
                  Backtest
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (parsedDesk.symbol) setChartSymbol(parsedDesk.symbol);
                    openIdePanel('chart');
                  }}
                  className="h-7 px-2.5 rounded bg-slate-700 hover:bg-slate-600 text-white text-[11px] font-semibold inline-flex items-center gap-1 shrink-0"
                  title="Show signal chart under the editor"
                >
                  <FontAwesomeIcon icon={faChartLine} className="text-[10px]" />
                  Chart
                </button>

              <div className="ml-auto flex items-center gap-0.5 shrink-0">
                {!idePopout && (
                  <button
                    type="button"
                    title="Open editor in a separate window"
                    onClick={openIdePopout}
                    className={`h-7 w-7 inline-flex items-center justify-center rounded ${
                      ideDark ? 'text-slate-400 hover:bg-white/10 hover:text-white' : 'text-gray-500 hover:bg-gray-200 hover:text-gray-800'
                    }`}
                  >
                    <FontAwesomeIcon icon={faExternalLinkAlt} className="text-[10px]" />
                  </button>
                )}
                {!idePopout && (
                <button
                  type="button"
                  title={ideFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen editor'}
                  onClick={toggleIdeFullscreen}
                  className={`h-7 w-7 inline-flex items-center justify-center rounded ${
                    ideFullscreen
                      ? ideDark
                        ? 'text-indigo-300 bg-white/5'
                        : 'text-indigo-600 bg-indigo-50'
                      : ideDark
                        ? 'text-slate-400 hover:bg-white/10 hover:text-white'
                        : 'text-gray-500 hover:bg-gray-200 hover:text-gray-800'
                  }`}
                >
                  <FontAwesomeIcon icon={ideFullscreen ? faCompress : faExpand} className="text-[10px]" />
                </button>
                )}
                {ideFullscreen && !idePopout && (
                  <button
                    type="button"
                    title="Close fullscreen"
                    onClick={() => setIdeFullscreen(false)}
                    className={`h-7 w-7 inline-flex items-center justify-center rounded ${
                      ideDark ? 'text-slate-400 hover:bg-white/10 hover:text-white' : 'text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    <FontAwesomeIcon icon={faTimes} className="text-[10px]" />
                  </button>
                )}
                <span className={`w-px h-4 mx-0.5 ${ideDark ? 'bg-white/10' : 'bg-gray-200'}`} />
                <button
                  type="button"
                  title={ideDocsOpen ? 'Hide docs panel' : 'Show strategy docs & API'}
                  onClick={() => setIdeDocsOpen((v) => !v)}
                  className={`h-7 w-7 inline-flex items-center justify-center rounded ${
                    ideDocsOpen
                      ? ideDark
                        ? 'text-indigo-300 bg-white/5'
                        : 'text-indigo-600 bg-indigo-50'
                      : ideDark
                        ? 'text-slate-400 hover:bg-white/10 hover:text-white'
                        : 'text-gray-500 hover:bg-gray-200 hover:text-gray-800'
                  }`}
                >
                  <FontAwesomeIcon icon={faBook} className="text-[10px]" />
                </button>
                <button
                  type="button"
                  title="AI coding assistant (local Ollama)"
                  onClick={() => {
                    setIdeDocsOpen(true);
                    setIdeDocsTab('assist');
                  }}
                  className={`h-7 w-7 inline-flex items-center justify-center rounded ${
                    ideDocsOpen && ideDocsTab === 'assist'
                      ? ideDark
                        ? 'text-indigo-300 bg-white/5'
                        : 'text-indigo-600 bg-indigo-50'
                      : ideDark
                        ? 'text-slate-400 hover:bg-white/10 hover:text-white'
                        : 'text-gray-500 hover:bg-gray-200 hover:text-gray-800'
                  }`}
                >
                  <FontAwesomeIcon icon={faRobot} className="text-[10px]" />
                </button>
                <button
                  type="button"
                  title={`Editor theme: ${ideTheme === 'robinhood' ? 'Robinhood' : ideTheme === 'dark' ? 'Desk dark' : 'Light'} (click to cycle)`}
                  onClick={() => setIdeTheme((v) => nextIdeTheme(v))}
                  className={`h-7 w-7 inline-flex items-center justify-center rounded ${
                    ideTheme === 'robinhood'
                      ? 'text-[#00C805] hover:bg-white/10'
                      : ideDark
                        ? 'text-amber-300 hover:bg-white/10'
                        : 'text-indigo-600 hover:bg-gray-200'
                  }`}
                >
                  <FontAwesomeIcon
                    icon={ideTheme === 'robinhood' ? faFeather : ideDark ? faMoon : faSun}
                    className="text-[11px]"
                  />
                </button>
                <span className={`w-px h-4 mx-0.5 ${ideDark ? 'bg-white/10' : 'bg-gray-200'}`} />
                <button
                  type="button"
                  title="Find"
                  onClick={() => codeEditorRef.current?.find()}
                  className={`h-7 w-7 inline-flex items-center justify-center rounded ${
                    ideDark ? 'text-slate-400 hover:bg-white/10 hover:text-white' : 'text-gray-500 hover:bg-gray-200 hover:text-gray-800'
                  }`}
                >
                  <FontAwesomeIcon icon={faSearch} className="text-[10px]" />
                </button>
                <button
                  type="button"
                  title="Replace"
                  onClick={() => codeEditorRef.current?.replace()}
                  className={`h-7 w-7 inline-flex items-center justify-center rounded ${
                    ideDark ? 'text-slate-400 hover:bg-white/10 hover:text-white' : 'text-gray-500 hover:bg-gray-200 hover:text-gray-800'
                  }`}
                >
                  <FontAwesomeIcon icon={faExchangeAlt} className="text-[10px]" />
                </button>
                <button
                  type="button"
                  title="Undo"
                  onClick={() => codeEditorRef.current?.undo()}
                  className={`h-7 w-7 inline-flex items-center justify-center rounded ${
                    ideDark ? 'text-slate-400 hover:bg-white/10 hover:text-white' : 'text-gray-500 hover:bg-gray-200 hover:text-gray-800'
                  }`}
                >
                  <FontAwesomeIcon icon={faUndo} className="text-[10px]" />
                </button>
                <button
                  type="button"
                  title="Redo"
                  onClick={() => codeEditorRef.current?.redo()}
                  className={`h-7 w-7 inline-flex items-center justify-center rounded ${
                    ideDark ? 'text-slate-400 hover:bg-white/10 hover:text-white' : 'text-gray-500 hover:bg-gray-200 hover:text-gray-800'
                  }`}
                >
                  <FontAwesomeIcon icon={faRedo} className="text-[10px]" />
                </button>
                <button
                  type="button"
                  title="Format document"
                  onClick={() => codeEditorRef.current?.format()}
                  className={`h-7 w-7 inline-flex items-center justify-center rounded ${
                    ideDark ? 'text-slate-400 hover:bg-white/10 hover:text-white' : 'text-gray-500 hover:bg-gray-200 hover:text-gray-800'
                  }`}
                >
                  <FontAwesomeIcon icon={faAlignLeft} className="text-[10px]" />
                </button>
                <button
                  type="button"
                  title="Toggle word wrap"
                  onClick={() => setEditorWordWrap((v) => !v)}
                  className={`h-7 w-7 inline-flex items-center justify-center rounded ${
                    editorWordWrap
                      ? ideDark
                        ? 'text-indigo-300 bg-white/5'
                        : 'text-indigo-600 bg-indigo-50'
                      : ideDark
                        ? 'text-slate-400 hover:bg-white/10 hover:text-white'
                        : 'text-gray-500 hover:bg-gray-200'
                  }`}
                >
                  <FontAwesomeIcon icon={faTextWidth} className="text-[10px]" />
                </button>
                <button
                  type="button"
                  title="Smaller font"
                  onClick={() => setEditorFontSize((n) => Math.max(11, n - 1))}
                  className={`h-7 w-7 inline-flex items-center justify-center rounded ${
                    ideDark ? 'text-slate-400 hover:bg-white/10 hover:text-white' : 'text-gray-500 hover:bg-gray-200 hover:text-gray-800'
                  }`}
                >
                  <FontAwesomeIcon icon={faMinus} className="text-[9px]" />
                </button>
                <span
                  className={`text-[10px] w-6 text-center tabular-nums ${ideDark ? 'text-slate-500' : 'text-gray-400'}`}
                  title="Font size"
                >
                  {editorFontSize}
                </span>
                <button
                  type="button"
                  title="Larger font"
                  onClick={() => setEditorFontSize((n) => Math.min(20, n + 1))}
                  className={`h-7 w-7 inline-flex items-center justify-center rounded ${
                    ideDark ? 'text-slate-400 hover:bg-white/10 hover:text-white' : 'text-gray-500 hover:bg-gray-200 hover:text-gray-800'
                  }`}
                >
                  <FontAwesomeIcon icon={faFont} className="text-[10px]" />
                </button>
              </div>
              </div>
            </div>

            {!syntaxCheck.ok && (
              <button
                type="button"
                onClick={() => {
                  if (syntaxCheck.line != null) {
                    codeEditorRef.current?.gotoLine?.(syntaxCheck.line, syntaxCheck.column || 1);
                  }
                }}
                className={`w-full text-left px-3 py-1.5 text-[11px] border-b flex items-start gap-2 ${
                  ideDark
                    ? 'bg-red-950/40 border-red-500/30 text-red-200'
                    : 'bg-red-50 border-red-200 text-red-800'
                }`}
                title="Jump to error"
              >
                <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5 shrink-0 text-[10px]" />
                <span className="min-w-0">
                  <span className="font-semibold">Syntax</span>
                  {syntaxCheck.line != null
                    ? ` · line ${syntaxCheck.line}${syntaxCheck.column != null ? `:${syntaxCheck.column}` : ''}`
                    : ''}
                  {': '}
                  <span className="break-words">{syntaxCheck.message}</span>
                </span>
              </button>
            )}

            <div className="flex-1 min-h-0 overflow-hidden">
              <AlgoCodeEditor
                ref={codeEditorRef}
                value={script}
                onChange={setScript}
                dark={ideDark}
                palette={ideTheme}
                fontSize={editorFontSize}
                wordWrap={editorWordWrap}
                height="100%"
              />
            </div>

            {/* IDE bottom: Output | Backtest | Chart */}
            <div
              className={`shrink-0 border-t flex flex-col min-h-0 ${ideDark ? 'border-white/10 bg-[var(--ide-editor)]' : 'border-gray-200 bg-white'}`}
              style={idePanelOpen ? { height: idePanelHeight } : undefined}
            >
              {idePanelOpen && (
                <div
                  role="separator"
                  aria-orientation="horizontal"
                  aria-label="Resize output panel"
                  title="Drag to resize · double-click to reset"
                  onPointerDown={startIdePanelResize}
                  onDoubleClick={() => {
                    setIdePanelHeight(IDE_PANEL_H_DEFAULT);
                    persistIdePanelHeight(IDE_PANEL_H_DEFAULT);
                    window.setTimeout(() => codeEditorRef.current?.layout?.(), 30);
                  }}
                  className={`h-1.5 shrink-0 cursor-row-resize flex items-center justify-center ${
                    ideDark ? 'bg-[var(--ide-sidebar)] hover:bg-indigo-500/40' : 'bg-gray-100 hover:bg-indigo-200'
                  }`}
                >
                  <span className={`block w-8 h-0.5 rounded ${ideDark ? 'bg-white/25' : 'bg-gray-400'}`} />
                </div>
              )}
              <div
                className={`h-8 px-2 flex items-center gap-1 border-b shrink-0 ${
                  ideDark ? 'border-white/10 bg-[var(--ide-sidebar)]' : 'border-gray-200 bg-gray-50'
                }`}
              >
                {[
                  { id: 'output', label: 'Output', icon: faTerminal },
                  { id: 'backtest', label: 'Backtest', icon: faFlask },
                  { id: 'chart', label: 'Chart', icon: faChartLine },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => {
                      if (ideBottomTab === tab.id && idePanelOpen) {
                        setIdePanelOpen(false);
                      } else {
                        setIdeBottomTab(tab.id);
                        setIdePanelOpen(true);
                        if (tab.id === 'backtest' && parsedDesk.symbol) {
                          setBacktestSymbol(parsedDesk.symbol);
                        }
                        if (tab.id === 'chart' && parsedDesk.symbol) {
                          setChartSymbol(parsedDesk.symbol);
                        }
                      }
                    }}
                    className={`h-6 px-2 rounded text-[10px] font-semibold uppercase tracking-wide inline-flex items-center gap-1.5 ${
                      idePanelOpen && ideBottomTab === tab.id
                        ? ideDark
                          ? 'bg-white/10 text-white'
                          : 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200'
                        : ideDark
                          ? 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
                          : 'text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <FontAwesomeIcon icon={tab.icon} className="text-[10px]" />
                    {tab.label}
                    {tab.id === 'output' ? (
                      <span className={`text-[9px] font-normal normal-case ${ideDark ? 'text-slate-500' : 'text-gray-400'}`}>
                        {scriptJobs.length}
                      </span>
                    ) : null}
                    {tab.id === 'backtest' && backtestResult ? (
                      <span className={`text-[9px] px-1 rounded normal-case font-medium ${ideDark ? 'bg-teal-500/20 text-teal-200' : 'bg-teal-100 text-teal-700'}`}>
                        done
                      </span>
                    ) : null}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setIdePanelOpen((v) => !v)}
                  className={`ml-auto h-6 px-2 rounded text-[10px] font-medium ${
                    ideDark ? 'text-slate-400 hover:bg-white/10' : 'text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {idePanelOpen ? 'Hide' : 'Show'}
                </button>
                {idePanelOpen && ideBottomTab === 'output' && (
                  <button
                    type="button"
                    onClick={clearLogs}
                    className={`h-6 px-2 rounded text-[10px] font-medium ${
                      ideDark
                        ? 'text-slate-400 hover:bg-white/10 hover:text-white'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Clear
                  </button>
                )}
              </div>

              {idePanelOpen && ideBottomTab === 'output' && (
                <div className="flex-1 min-h-0 flex flex-col">
                  <div className={`px-2 py-1.5 border-b shrink-0 ${ideDark ? 'border-white/5 bg-[var(--ide-inset)]' : 'border-gray-100 bg-gray-50'}`}>
                    {scriptJobs.length === 0 ? (
                      <p className={`text-[11px] py-0.5 ${ideDark ? 'text-slate-500' : 'text-gray-500'}`}>
                        No scripts running. Click Start to launch one — several can run in parallel.
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {scriptJobs.map((job) => {
                          const attached = attachedIds.has(job.id);
                          const selected = logTab === job.id;
                          return (
                            <div
                              key={job.id}
                              className={`inline-flex items-center gap-1.5 rounded px-2 py-1 text-[10px] border ${
                                selected
                                  ? ideDark
                                    ? 'bg-indigo-500/25 border-indigo-400/40 text-indigo-100'
                                    : 'bg-indigo-50 border-indigo-200 text-indigo-900'
                                  : ideDark
                                    ? 'border-white/10 text-slate-300'
                                    : 'border-gray-200 text-gray-700 bg-white'
                              }`}
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${attached ? 'bg-emerald-400' : 'bg-amber-400'}`}
                                title={attached ? 'live' : 'detached'}
                              />
                              <button
                                type="button"
                                onClick={() => selectLogTab(job.id)}
                                className="font-medium truncate max-w-[120px]"
                                title={job.id}
                              >
                                {jobLabel(job)}
                              </button>
                              <span className={`${ideDark ? 'text-slate-500' : 'text-gray-400'}`}>
                                {job.intervalSeconds || '—'}s
                              </span>
                              <button
                                type="button"
                                onClick={() => handleStop(job.id)}
                                className={`px-1 rounded ${ideDark ? 'text-red-300 hover:bg-white/10' : 'text-red-600 hover:bg-red-50'}`}
                                title="Stop"
                              >
                                <FontAwesomeIcon icon={faStop} className="text-[9px]" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className={`px-2 pt-1.5 flex flex-wrap gap-1 border-b shrink-0 ${ideDark ? 'border-white/5' : 'border-gray-100'}`}>
                    <button
                      type="button"
                      onClick={() => selectLogTab('__system__')}
                      className={`px-2 py-1 rounded-t text-[11px] font-medium border border-b-0 ${
                        logTab === '__system__' || !logTab
                          ? ideDark
                            ? 'bg-[var(--ide-editor)] text-slate-100 border-white/15'
                            : 'bg-white text-gray-900 border-gray-200'
                          : ideDark
                            ? 'bg-transparent text-slate-400 border-transparent hover:text-slate-200'
                            : 'bg-transparent text-gray-600 border-transparent hover:bg-gray-100'
                      }`}
                    >
                      System
                      {systemLogs.length ? ` (${systemLogs.length})` : ''}
                    </button>
                    {[...new Set([
                      ...scriptJobs.map((j) => j.id),
                      ...Object.keys(logsByAlgo),
                    ])].map((id) => {
                      const running = scriptJobs.some((j) => j.id === id);
                      const count = (logsByAlgo[id] || []).length;
                      const label = getStoredLabel(id) || scriptJobs.find((j) => j.id === id)?.label || id.replace(/^algo-/, '…');
                      const selected = logTab === id;
                      return (
                        <div
                          key={id}
                          className={`inline-flex items-center max-w-[180px] rounded-t border border-b-0 ${
                            selected
                              ? ideDark
                                ? 'bg-[var(--ide-editor)] text-slate-100 border-white/15'
                                : 'bg-white text-gray-900 border-gray-200'
                              : ideDark
                                ? 'bg-transparent text-slate-400 border-transparent hover:text-slate-200'
                                : 'bg-transparent text-gray-600 border-transparent hover:bg-gray-100'
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => selectLogTab(id)}
                            className="px-2 py-1 text-[11px] font-medium truncate min-w-0"
                            title={id}
                          >
                            <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${running ? 'bg-green-400' : 'bg-gray-400'}`} />
                            {label}
                            {count ? ` (${count})` : ''}
                          </button>
                          {!running && (
                            <button
                              type="button"
                              onClick={(e) => closeLogTab(id, e)}
                              title="Close tab"
                              className={`h-5 w-5 mr-0.5 inline-flex items-center justify-center rounded shrink-0 ${
                                ideDark ? 'text-slate-500 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-800 hover:bg-gray-200'
                              }`}
                            >
                              <FontAwesomeIcon icon={faTimes} className="text-[8px]" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className={`p-2.5 flex-1 min-h-0 overflow-y-auto font-mono text-[11px] leading-relaxed ${
                    ideDark ? 'bg-[var(--ide-editor)] text-slate-300' : 'bg-white text-gray-800'
                  }`}>
                    {activeLogs.length === 0 ? (
                      <div className={`text-center py-6 ${ideDark ? 'text-slate-500' : 'text-gray-400'}`}>
                        No logs in this tab yet. Start an algorithm or select another run.
                      </div>
                    ) : (
                      <div className="space-y-0.5">
                        {activeLogs.map((log, index) => (
                          <div
                            key={`${log.timestamp}-${index}`}
                            className={
                              log.type === 'error'
                                ? ideDark ? 'text-red-400' : 'text-red-700'
                                : log.type === 'warn'
                                  ? ideDark ? 'text-amber-300' : 'text-amber-700'
                                  : log.type === 'status'
                                    ? ideDark ? 'text-sky-300' : 'text-sky-700'
                                    : ideDark ? 'text-slate-300' : 'text-gray-800'
                            }
                          >
                            <span className={ideDark ? 'text-slate-500' : 'text-gray-400'}>
                              [{formatTimestamp(log.timestamp)}]
                            </span>{' '}
                            <span className="font-semibold">
                              {log.type === 'error' ? 'ERROR:' :
                               log.type === 'warn' ? 'WARN:' :
                               log.type === 'status' ? 'STATUS:' : ''}
                            </span>{' '}
                            {log.message}
                          </div>
                        ))}
                        <div ref={logsEndRef} />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {idePanelOpen && ideBottomTab === 'backtest' && (
                <div className="flex-1 min-h-0 overflow-y-auto p-3">
                  <div className="bg-white rounded-lg border border-teal-100 p-3 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
                <FontAwesomeIcon icon={faFlask} className="text-teal-700" />
                Backtest (simulated)
              </h3>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Replays the editor script — templates or your custom/saved algorithms — bar-by-bar with fake fills. No live or paper orders.
                Starting capital becomes the simulated account (<code className="bg-gray-100 px-1 rounded">getAccount().cash</code> / equity) and caps buys.
                Tickers come from the IDE <strong>Symbols</strong> field (first name is the primary). TWAP is not supported.
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleBacktest()}
              disabled={backtestRunning}
              className="px-3 py-1.5 bg-teal-700 text-white rounded-lg hover:bg-teal-800 disabled:opacity-50 text-xs font-medium flex items-center gap-1.5"
            >
              {backtestRunning ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                  Running…
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPlay} />
                  Run backtest
                </>
              )}
            </button>
          </div>

          <div className="flex flex-wrap gap-3 items-end">
            <p className="text-[10px] text-gray-500 pb-1">
              Primary: <span className="font-semibold text-gray-800">{parsedDesk.symbol || '—'}</span>
              {parsedDesk.symbols.length > 1 ? ` · also ${parsedDesk.symbols.slice(1).join(', ')}` : ''}
              <span className="text-gray-400"> (edit in the toolbar)</span>
            </p>
            <label className="text-[10px] text-gray-600">
              Timeframe
              <select
                value={backtestTimeframe}
                onChange={(e) => {
                  const tf = e.target.value;
                  setBacktestTimeframe(tf);
                  const range = defaultBacktestRange(tf);
                  setBacktestStart(range.start);
                  setBacktestEnd(range.end);
                  if (isIntradayBacktestTf(tf)) {
                    setBacktestStartTime(range.startTime);
                    setBacktestEndTime(range.endTime);
                  }
                }}
                className="mt-0.5 block px-2 py-1 text-xs border border-gray-300 rounded"
              >
                <option value="1Day">1Day</option>
                <option value="1Hour">1Hour</option>
                <option value="15Min">15Min</option>
                <option value="5Min">5Min</option>
                <option value="1Min">1Min</option>
              </select>
            </label>
            <label className="text-[10px] text-gray-600">
              Start date
              <input
                type="date"
                value={backtestStart}
                onChange={(e) => setBacktestStart(e.target.value)}
                className="mt-0.5 block px-2 py-1 text-xs border border-gray-300 rounded"
              />
            </label>
            {isIntradayBacktestTf(backtestTimeframe) && (
              <label className="text-[10px] text-gray-600">
                Start time (ET)
                <input
                  type="time"
                  value={backtestStartTime}
                  onChange={(e) => setBacktestStartTime(e.target.value)}
                  className="mt-0.5 block px-2 py-1 text-xs border border-gray-300 rounded"
                  title="America/New_York wall clock"
                />
              </label>
            )}
            <label className="text-[10px] text-gray-600">
              End date
              <input
                type="date"
                value={backtestEnd}
                onChange={(e) => setBacktestEnd(e.target.value)}
                className="mt-0.5 block px-2 py-1 text-xs border border-gray-300 rounded"
              />
            </label>
            {isIntradayBacktestTf(backtestTimeframe) && (
              <label className="text-[10px] text-gray-600">
                End time (ET)
                <input
                  type="time"
                  value={backtestEndTime}
                  onChange={(e) => setBacktestEndTime(e.target.value)}
                  className="mt-0.5 block px-2 py-1 text-xs border border-gray-300 rounded"
                  title="America/New_York wall clock"
                />
              </label>
            )}
            <label className="text-[10px] text-gray-600">
              Starting capital ($)
              <input
                type="number"
                min="1000"
                step="1000"
                value={backtestCash}
                onChange={(e) => setBacktestCash(e.target.value)}
                className="mt-0.5 block w-28 px-2 py-1 text-xs border border-gray-300 rounded"
                title="Seeded into simulated getAccount().cash / equity. Buys are sized and capped against this."
              />
            </label>
            <label
              className="flex items-center gap-1.5 text-[10px] text-gray-700 pb-1 cursor-pointer"
              title="Scale fixed qty/notional in scripts vs a $100k baseline (e.g. qty 10 with $50k → 5 shares)"
            >
              <input
                type="checkbox"
                checked={backtestScalePositions}
                onChange={(e) => setBacktestScalePositions(e.target.checked)}
                className="rounded border-gray-300"
              />
              Scale size with capital
            </label>
            <label
              className="flex items-center gap-1.5 text-[10px] text-gray-700 pb-1 cursor-pointer"
              title="Ignore local bars cache and re-fetch from Alpaca (cache still updates afterward)"
            >
              <input
                type="checkbox"
                checked={backtestForceRefresh}
                onChange={(e) => setBacktestForceRefresh(e.target.checked)}
                className="rounded border-gray-300"
              />
              Refresh market data
            </label>
          </div>

          {backtestError && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded px-2 py-1.5">
              {backtestError}
            </div>
          )}

          {backtestResult && (
            <div className="space-y-3 border-t border-teal-50 pt-3">
              <div className="flex flex-wrap items-baseline gap-2 text-xs text-gray-600">
                {backtestResult.label && (
                  <span className="font-semibold text-teal-800 bg-teal-50 border border-teal-100 px-1.5 py-0.5 rounded">
                    {backtestResult.label}
                  </span>
                )}
                <span className="font-semibold text-gray-800">{backtestResult.symbol}</span>
                {backtestResult.symbolSource === 'form' && backtestResult.scriptSymbol && backtestResult.scriptSymbol !== backtestResult.symbol && (
                  <span className="text-teal-700">(overrode script {backtestResult.scriptSymbol})</span>
                )}
                <span>{backtestResult.timeframe}</span>
                <span>
                  {formatEtRange(backtestResult.start, backtestResult.end, {
                    intraday: Boolean(backtestResult.startTime || backtestResult.endTime),
                  })}
                </span>
                <span>· {backtestResult.barsUsed} bars</span>
                {backtestResult.barsCache && (
                  <span
                    className="text-gray-500"
                    title={Object.entries(backtestResult.barsCache)
                      .map(([s, src]) => `${s}: ${src}`)
                      .join(', ')}
                  >
                    · data:{' '}
                    {Object.values(backtestResult.barsCache).every((s) => s === 'cache')
                      ? 'cached'
                      : Object.values(backtestResult.barsCache).every((s) => s === 'network')
                        ? 'fetched'
                        : 'mixed cache'}
                  </span>
                )}
                {backtestResult.symbols?.length > 1 && (
                  <span>· symbols: {backtestResult.symbols.join(', ')}</span>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
                {[
                  {
                    label: 'Return',
                    value: `${backtestResult.metrics.totalReturnPct >= 0 ? '+' : ''}${backtestResult.metrics.totalReturnPct}%`,
                    good: backtestResult.metrics.totalReturnPct >= 0,
                  },
                  {
                    label: 'Buy & hold',
                    value:
                      backtestResult.metrics.buyHoldReturnPct != null
                        ? `${backtestResult.metrics.buyHoldReturnPct >= 0 ? '+' : ''}${backtestResult.metrics.buyHoldReturnPct}%`
                        : '—',
                    good: (backtestResult.metrics.buyHoldReturnPct ?? 0) >= 0,
                  },
                  {
                    label: 'vs B&H',
                    value:
                      backtestResult.metrics.alphaPct != null
                        ? `${backtestResult.metrics.alphaPct >= 0 ? '+' : ''}${backtestResult.metrics.alphaPct}%`
                        : '—',
                    good: (backtestResult.metrics.alphaPct ?? 0) >= 0,
                  },
                  {
                    label: 'Max DD',
                    value: `-${backtestResult.metrics.maxDrawdownPct}%`,
                    good: false,
                  },
                  {
                    label: 'Win rate',
                    value:
                      backtestResult.metrics.winRatePct != null
                        ? `${backtestResult.metrics.winRatePct}%`
                        : '—',
                    good: (backtestResult.metrics.winRatePct ?? 0) >= 50,
                  },
                  {
                    label: 'Trades',
                    value: String(backtestResult.metrics.tradeCount),
                    good: true,
                  },
                ].map((m) => (
                  <div key={m.label} className="rounded border border-gray-200 bg-gray-50 px-2 py-1.5">
                    <div className="text-[10px] uppercase text-gray-500 font-semibold">{m.label}</div>
                    <div
                      className={`text-sm font-bold tabular-nums ${
                        m.label === 'Max DD'
                          ? 'text-amber-700'
                          : m.good
                            ? 'text-emerald-700'
                            : 'text-red-700'
                      }`}
                    >
                      {m.value}
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <div className="text-[10px] font-semibold uppercase text-gray-500 mb-1">
                  Equity · ${backtestResult.metrics.initialCash.toLocaleString()} → $
                  {backtestResult.metrics.finalEquity.toLocaleString()}
                </div>
                <div className="rounded border border-gray-200 bg-white px-1">
                  <EquitySparkline points={backtestResult.equityCurve || []} />
                </div>
              </div>

              {(backtestResult.logs || []).length > 0 ? (
                <div>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="text-[10px] font-semibold uppercase text-gray-500">
                      Run log ({backtestResult.logs.length})
                    </div>
                  </div>
                  <div className="max-h-48 overflow-auto rounded border border-gray-200 bg-gray-950 text-[11px] font-mono p-2 space-y-0.5">
                    {backtestResult.logs.map((log, index) => (
                      <div
                        key={`bt-log-${index}-${log.timestamp}`}
                        className={
                          log.type === 'error'
                            ? 'text-red-400'
                            : log.type === 'warn'
                              ? 'text-amber-300'
                              : log.type === 'status'
                                ? 'text-sky-300'
                                : 'text-slate-200'
                        }
                      >
                        <span className="text-slate-500">
                          [{formatEtDateTime(log.timestamp, { seconds: true })} ET]
                        </span>{' '}
                        {log.type === 'error' ? (
                          <span className="font-semibold">ERROR: </span>
                        ) : log.type === 'warn' ? (
                          <span className="font-semibold">WARN: </span>
                        ) : log.type === 'status' ? (
                          <span className="font-semibold">STATUS: </span>
                        ) : null}
                        {log.message}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-[11px] text-gray-500 border border-dashed border-gray-200 rounded px-2 py-1.5">
                  No run log lines (quiet bars are filtered). Use <code className="bg-gray-100 px-1 rounded">console.log</code> for fills/signals you want to see.
                </p>
              )}

              <div>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                  <div className="text-[10px] font-semibold uppercase text-gray-500">
                    Signal chart ·{' '}
                    {(backtestResult.symbols?.length
                      ? backtestResult.symbols
                      : [backtestResult.symbol]
                    )
                      .filter(Boolean)
                      .join(' · ')}
                    {backtestChartEvents.length
                      ? ` · ${backtestChartEvents.length} marker${backtestChartEvents.length === 1 ? '' : 's'}`
                      : ''}
                  </div>
                  <button
                    type="button"
                    onClick={() => openIdePanel('chart')}
                    className="text-[10px] font-semibold text-teal-700 hover:text-teal-900"
                  >
                    Open in Chart tab
                  </button>
                </div>
                <AlgoSignalChart
                  symbol={backtestResult.symbol || chartSymbol}
                  symbols={
                    backtestResult.symbols?.length
                      ? backtestResult.symbols
                      : parsedDesk.symbols
                  }
                  templateId={selectedTemplateId}
                  templateName={
                    templates.find((t) => t.id === selectedTemplateId)?.name || ''
                  }
                  params={templates.find((t) => t.id === selectedTemplateId)?.params || {}}
                  script={script}
                  liveEvents={[]}
                  backtestRange={backtestChartRange}
                  backtestEvents={backtestChartEvents}
                  height={280}
                />
              </div>

              {(backtestResult.trades || []).length > 0 && (
                <div>
                  <div className="text-[10px] font-semibold uppercase text-gray-500 mb-1">
                    Trades ({backtestResult.trades.length})
                  </div>
                  <div className="max-h-40 overflow-auto rounded border border-gray-200">
                    <table className="w-full text-[11px]">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr className="text-left text-gray-500">
                          <th className="px-2 py-1 font-semibold">Time</th>
                          <th className="px-2 py-1 font-semibold">Symbol</th>
                          <th className="px-2 py-1 font-semibold">Side</th>
                          <th className="px-2 py-1 font-semibold">Qty</th>
                          <th className="px-2 py-1 font-semibold">Price</th>
                          <th className="px-2 py-1 font-semibold">Reason</th>
                          <th className="px-2 py-1 font-semibold">P/L</th>
                        </tr>
                      </thead>
                      <tbody>
                        {backtestResult.trades.map((t) => (
                          <tr key={t.id} className="border-t border-gray-100">
                            <td className="px-2 py-1 whitespace-nowrap text-gray-600">
                              {formatEtDateTime(t.time)} ET
                            </td>
                            <td className="px-2 py-1 font-semibold text-gray-800">
                              {t.symbol || backtestResult.symbol}
                            </td>
                            <td
                              className={`px-2 py-1 font-semibold uppercase ${
                                t.side === 'buy' ? 'text-emerald-700' : 'text-red-700'
                              }`}
                            >
                              {t.side}
                            </td>
                            <td className="px-2 py-1 tabular-nums">{t.qty}</td>
                            <td className="px-2 py-1 tabular-nums">${Number(t.price).toFixed(2)}</td>
                            <td className="px-2 py-1 text-gray-500">{t.reason}</td>
                            <td
                              className={`px-2 py-1 tabular-nums ${
                                t.realizedPl > 0
                                  ? 'text-emerald-700'
                                  : t.realizedPl < 0
                                    ? 'text-red-700'
                                    : 'text-gray-500'
                              }`}
                            >
                              {t.realizedPl ? t.realizedPl.toFixed(2) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {(() => {
                const opens =
                  (backtestResult.openPositions || []).length > 0
                    ? backtestResult.openPositions
                    : backtestResult.openPosition
                      ? [backtestResult.openPosition]
                      : [];
                if (!opens.length) return null;
                return (
                  <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-100 rounded px-2 py-1.5 space-y-1">
                    <div className="font-semibold uppercase text-[10px] text-amber-800 tracking-wide">
                      Still open at end ({opens.length} leg{opens.length === 1 ? '' : 's'})
                    </div>
                    <ul className="space-y-0.5">
                      {opens.map((p) => {
                        const qty = Number(p.qty);
                        const side = qty < 0 || p.side === 'short' ? 'short' : 'long';
                        return (
                          <li key={p.symbol || 'pos'} className="tabular-nums">
                            <span className="font-semibold">{p.symbol || backtestResult.symbol}</span>
                            {' · '}
                            <span className={side === 'short' ? 'text-red-700' : 'text-emerald-800'}>
                              {side} {Math.abs(qty)}
                            </span>
                            {' @ avg $'}
                            {Number(p.avg_entry_price).toFixed(2)}
                            {p.current_price != null
                              ? ` · mark $${Number(p.current_price).toFixed(2)}`
                              : ''}
                            {p.unrealized_pl != null
                              ? ` · uPL ${Number(p.unrealized_pl) >= 0 ? '+' : ''}${Number(p.unrealized_pl).toFixed(2)}`
                              : ''}
                          </li>
                        );
                      })}
                    </ul>
                    <div className="text-amber-700/80 text-[10px]">Marked in final equity</div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
                </div>
              )}

              {idePanelOpen && ideBottomTab === 'chart' && (
                <div className="flex-1 min-h-0 overflow-y-auto p-3">
                  <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="text-[10px] font-semibold text-gray-500 uppercase">Chart</span>
          <span className="text-[11px] font-semibold text-gray-800">
            {(backtestResult?.symbols?.length
              ? backtestResult.symbols
              : parsedDesk.symbols?.length
                ? parsedDesk.symbols
                : [backtestResult?.symbol || parsedDesk.symbol || chartSymbol]
            )
              .filter(Boolean)
              .join(' · ')}
          </span>
          <span className="text-[10px] text-gray-500">
            {backtestResult
              ? 'Showing signals from the latest backtest for that date range.'
              : 'Uses the IDE Symbols field. Live markers appear while an algo is running.'}
          </span>
          {backtestResult && (
            <button
              type="button"
              onClick={() => openIdePanel('backtest')}
              className="text-[10px] font-semibold text-teal-700 hover:text-teal-900"
            >
              Back to Backtest
            </button>
          )}
        </div>
        <AlgoSignalChart
          symbol={backtestResult?.symbol || chartSymbol}
          symbols={
            backtestResult?.symbols?.length
              ? backtestResult.symbols
              : parsedDesk.symbols
          }
          templateId={selectedTemplateId}
          templateName={
            templates.find((t) => t.id === selectedTemplateId)?.name || ''
          }
          params={templates.find((t) => t.id === selectedTemplateId)?.params || {}}
          script={script}
          liveEvents={backtestResult ? [] : signalEvents}
          backtestRange={backtestChartRange}
          backtestEvents={backtestChartEvents}
        />
      </div>
                </div>
              )}
            </div>
          </div>
          <AlgoWorkbenchDocs
            open={ideDocsOpen}
            onToggle={() => setIdeDocsOpen((v) => !v)}
            dark={ideDark}
            template={templates.find((t) => t.id === selectedTemplateId) || null}
            templates={templates}
            onLoadTemplate={handleLoadTemplate}
            activeTab={ideDocsTab}
            onActiveTab={setIdeDocsTab}
            script={script}
            symbols={deskSymbols}
            onApplyScript={(code) => {
              if (!code) return;
              setScript(code);
              setSelectedTemplateId('');
              addLog('status', 'Applied Ollama-generated script to the editor');
            }}
          />
        </div>
      </div>

        </>
      )}

      {workspace === 'twap' && (
        <>
      {/* TWAP launcher */}
      <div className="mb-3 bg-white rounded-lg shadow-md border border-gray-100 p-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold text-gray-800">TWAP execution (server-side)</h3>
          <span className="text-[10px] text-gray-500">
            Venue: {executionVenue === 'schwab' ? `Schwab ${schwabAccountNumber || '(pick account)'}` : 'Alpaca'}. Runs on the backend.
          </span>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <label className="block text-[10px] text-gray-500 mb-0.5">Symbol</label>
            <input
              value={twapForm.symbol}
              onChange={(e) => setTwapForm((f) => ({ ...f, symbol: e.target.value.toUpperCase() }))}
              className="w-24 px-2 py-1 text-xs border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-0.5">Side</label>
            <select
              value={twapForm.side}
              onChange={(e) => setTwapForm((f) => ({ ...f, side: e.target.value }))}
              className="px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="buy">Buy</option>
              <option value="sell">Sell</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-0.5">Qty</label>
            <input
              type="number"
              value={twapForm.qty}
              onChange={(e) => setTwapForm((f) => ({ ...f, qty: e.target.value }))}
              className="w-20 px-2 py-1 text-xs border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-0.5">Minutes</label>
            <input
              type="number"
              value={twapForm.durationMinutes}
              onChange={(e) => setTwapForm((f) => ({ ...f, durationMinutes: e.target.value }))}
              className="w-20 px-2 py-1 text-xs border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-0.5">Slices</label>
            <input
              type="number"
              value={twapForm.slices}
              onChange={(e) => setTwapForm((f) => ({ ...f, slices: e.target.value }))}
              className="w-16 px-2 py-1 text-xs border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-0.5">Limit ±$</label>
            <input
              type="number"
              step="0.01"
              placeholder="mkt"
              value={twapForm.limitOffset}
              onChange={(e) => setTwapForm((f) => ({ ...f, limitOffset: e.target.value }))}
              className="w-20 px-2 py-1 text-xs border border-gray-300 rounded"
            />
          </div>
          <button
            type="button"
            onClick={handleStartTwap}
            className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-xs font-medium"
          >
            Start TWAP
          </button>
          <button
            type="button"
            onClick={handleStopTwap}
            disabled={!activeTwapId}
            className="px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-xs font-medium disabled:opacity-50"
          >
            Stop TWAP
          </button>
        </div>
        {runningJobs.length > 0 && (
          <div className="mt-2 text-[10px] text-gray-600">
            Running jobs: {runningJobs.map((j) => `${j.kind || 'job'}:${j.id}`).join(', ')}
          </div>
        )}

        {/* TWAP slice dashboard */}
        <div className="mt-3 border-t border-gray-100 pt-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wide">
              TWAP order dashboard
            </h4>
            <span className="text-[10px] text-gray-500">
              Server-side slicer · polls every 3s · prices from submit / fill
            </span>
          </div>
          {twapJobs.length === 0 ? (
            <p className="text-xs text-gray-500 py-2">
              No TWAP jobs yet. Start one above — each buy/sell slice will show status and price here.
            </p>
          ) : (
            <div className="space-y-3">
              {twapJobs.map((job) => {
                const meta = job.meta || {};
                const pct = job.progress?.pct ?? 0;
                const statusColor =
                  job.status === 'running'
                    ? 'bg-indigo-100 text-indigo-800'
                    : job.status === 'completed'
                      ? 'bg-green-100 text-green-800'
                      : job.status === 'cancelled'
                        ? 'bg-gray-200 text-gray-700'
                        : 'bg-red-100 text-red-800';
                return (
                  <div key={job.id} className="rounded-lg border border-gray-200 overflow-hidden">
                    <div className="px-2.5 py-2 bg-gray-50 flex flex-wrap items-center gap-2 text-xs">
                      <span className={`px-1.5 py-0.5 rounded font-semibold ${statusColor}`}>
                        {job.status}
                      </span>
                      <span className="font-semibold text-gray-900">
                        {meta.side?.toUpperCase()} {meta.qty} {meta.symbol}
                      </span>
                      <span className="text-gray-500">
                        {job.progress?.done ?? 0}/{job.progress?.total ?? meta.slices ?? '?'} slices
                        {job.filledQty != null ? ` · filled ${job.filledQty}` : ''}
                        {job.remaining != null ? ` · left ${job.remaining}` : ''}
                      </span>
                      <span className="text-[10px] text-gray-400 font-mono ml-auto">{job.id}</span>
                      {job.status === 'running' && (
                        <button
                          type="button"
                          onClick={() => handleStopTwap(job.id)}
                          className="px-2 py-0.5 bg-gray-700 text-white rounded text-[10px]"
                        >
                          Stop
                        </button>
                      )}
                    </div>
                    <div className="h-1.5 bg-gray-100">
                      <div
                        className="h-full bg-indigo-500 transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-[11px]">
                        <thead className="bg-white text-gray-500 text-left">
                          <tr>
                            <th className="px-2 py-1.5 font-semibold">#</th>
                            <th className="px-2 py-1.5 font-semibold">State</th>
                            <th className="px-2 py-1.5 font-semibold">Side</th>
                            <th className="px-2 py-1.5 font-semibold">Qty</th>
                            <th className="px-2 py-1.5 font-semibold">Last / limit</th>
                            <th className="px-2 py-1.5 font-semibold">Fill px</th>
                            <th className="px-2 py-1.5 font-semibold">Order</th>
                            <th className="px-2 py-1.5 font-semibold">When</th>
                            <th className="px-2 py-1.5 font-semibold">Note</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(job.slices || []).map((s) => {
                            const st =
                              s.status === 'filled'
                                ? 'text-green-700'
                                : s.status === 'error'
                                  ? 'text-red-600'
                                  : s.status === 'submitted'
                                    ? 'text-indigo-700'
                                    : s.status === 'skipped' || s.status === 'cancelled'
                                      ? 'text-gray-400'
                                      : 'text-amber-700';
                            const px = (v) =>
                              v != null && Number.isFinite(Number(v))
                                ? `$${Number(v).toFixed(2)}`
                                : '—';
                            return (
                              <tr key={s.index} className="border-t border-gray-100">
                                <td className="px-2 py-1 font-mono">{s.index}</td>
                                <td className={`px-2 py-1 font-semibold ${st}`}>{s.status}</td>
                                <td className="px-2 py-1 uppercase">{s.side}</td>
                                <td className="px-2 py-1 num">{s.qty ?? '—'}</td>
                                <td className="px-2 py-1 num">
                                  {px(s.submittedPrice)}
                                  {s.limitPrice != null ? ` / ${px(s.limitPrice)}` : ''}
                                </td>
                                <td className="px-2 py-1 num font-semibold">{px(s.filledAvgPrice)}</td>
                                <td className="px-2 py-1 font-mono text-[10px] text-gray-500">
                                  {s.orderId ? `${String(s.orderId).slice(0, 8)}…` : '—'}
                                  {s.orderStatus ? ` (${s.orderStatus})` : ''}
                                </td>
                                <td className="px-2 py-1 text-gray-500 whitespace-nowrap">
                                  {s.executedAt
                                    ? new Date(s.executedAt).toLocaleTimeString()
                                    : s.scheduledAt
                                      ? `~${new Date(s.scheduledAt).toLocaleTimeString()}`
                                      : '—'}
                                </td>
                                <td className="px-2 py-1 text-gray-600 max-w-[180px] truncate" title={s.message}>
                                  {s.message}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>


        </>
      )}
{workspace === 'reference' && (
        <>
      {/* How-to guides */}
      <details className="mb-3 bg-white rounded-lg shadow-md border border-gray-100" open>
        <summary className="px-3 py-2 cursor-pointer text-sm font-bold text-gray-800 hover:bg-gray-50">
          How to use each algorithm
          {selectedTemplateId ? (
            <span className="ml-2 font-normal text-indigo-600 text-xs">
              · focused: {templates.find((t) => t.id === selectedTemplateId)?.name || selectedTemplateId}
            </span>
          ) : null}
        </summary>
        <div className="px-3 pb-3 space-y-3 border-t border-gray-100">
          <p className="text-xs text-gray-600 pt-2">
            The same guides live in the workbench <strong>Docs</strong> panel (right side, book icon). Pick a template above to load its script, or read all guides below. Kill never flattens your whole account —
            ownership is tracked from orders the algo runner places in this server session.
          </p>
          {(selectedTemplateId
            ? templates.filter((t) => t.id === selectedTemplateId)
            : templates
          ).map((t) => (
            <div key={t.id} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="flex flex-wrap items-baseline gap-2 mb-1">
                <h4 className="text-sm font-bold text-gray-900">{t.name}</h4>
                {t.category && (
                  <span className="text-[10px] uppercase tracking-wide text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded">
                    {t.category}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-700 mb-2">{t.guide?.summary || t.description}</p>
              {t.guide?.howItWorks?.length > 0 && (
                <div className="mb-2">
                  <div className="text-[10px] font-semibold uppercase text-gray-500 mb-1">How it works</div>
                  <ul className="list-disc pl-4 space-y-0.5 text-xs text-gray-700">
                    {t.guide.howItWorks.map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                </div>
              )}
              {t.guide?.howToRun?.length > 0 && (
                <div className="mb-2">
                  <div className="text-[10px] font-semibold uppercase text-gray-500 mb-1">How to run</div>
                  <ol className="list-decimal pl-4 space-y-0.5 text-xs text-gray-700">
                    {t.guide.howToRun.map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ol>
                </div>
              )}
              {t.guide?.parameters?.length > 0 && (
                <div className="mb-2">
                  <div className="text-[10px] font-semibold uppercase text-gray-500 mb-1">Parameters</div>
                  <ul className="space-y-0.5 text-xs text-gray-700">
                    {t.guide.parameters.map((p, i) => (
                      <li key={i}>
                        <code className="bg-white border border-gray-200 px-1 rounded text-[11px]">{p.name}</code>
                        {' — '}
                        {p.meaning}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {t.guide?.risks?.length > 0 && (
                <div>
                  <div className="text-[10px] font-semibold uppercase text-amber-700 mb-1">Risks / caveats</div>
                  <ul className="list-disc pl-4 space-y-0.5 text-xs text-gray-700">
                    {t.guide.risks.map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                </div>
              )}
              {!selectedTemplateId && (
                <button
                  type="button"
                  className="mt-2 text-xs text-indigo-600 hover:underline"
                  onClick={() => handleLoadTemplate(t.id)}
                >
                  Load this template →
                </button>
              )}
            </div>
          ))}
          {templates.length === 0 && (
            <p className="text-xs text-gray-500 py-2">Templates loading…</p>
          )}
        </div>
      </details>


      <div className="mb-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5">
        <h3 className="text-sm font-bold text-blue-900 flex items-center">
          <FontAwesomeIcon icon={faCode} className="mr-1.5 text-xs" />
          API &amp; helpers reference
        </h3>
        <p className="text-xs text-blue-800 mt-1.5 leading-relaxed">
          Full signatures, parameters, and return shapes are in the workbench <strong>Docs</strong> panel → <strong>API</strong> tab
          (book icon on the editor toolbar). Open Workbench, then click the book to show or hide the pane.
        </p>
        <p className="text-[11px] text-blue-700 mt-1">
          Alpaca broker docs:{' '}
          <a
            href="https://docs.alpaca.markets/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            docs.alpaca.markets
          </a>
        </p>
      </div>


        </>
      )}
      {/* Save Algorithm Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-4 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-800 mb-3">
              {selectedAlgorithm ? 'Update Algorithm' : 'Add Custom Algorithm'}
            </h3>
            
            <p className="text-xs text-gray-500 mb-3">
              {selectedAlgorithm
                ? 'Update the name/description for this saved algorithm. The script in the editor will be stored.'
                : 'Give your custom script a name to add it to Saved Algorithms. You can edit the code in the editor anytime, then save again.'}
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={algorithmName}
                  onChange={(e) => setAlgorithmName(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., My custom strategy"
                  autoFocus
                />
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={algorithmDescription}
                  onChange={(e) => setAlgorithmDescription(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                  rows="3"
                  placeholder="Brief description of the algorithm..."
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() => setShowSaveModal(false)}
                className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-xs font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAlgorithm}
                disabled={saving || !algorithmName.trim()}
                className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
              >
                {saving ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1" />
                    Saving...
                  </>
                ) : (
                  selectedAlgorithm ? 'Update' : 'Add algorithm'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default AlgorithmicTradingPage;

